/**
 * API Client for Backend Communication
 *
 * Centralized API client with type-safe methods for all backend endpoints.
 * Handles authentication, error handling, and request/response serialization.
 */

import { supabase } from './supabase'
import { trackDataFastActivationEvent } from './analytics'
import { API_BASE_URL } from './apiBaseUrl'
import { PAYMENT_REQUIRED_EVENT } from './paymentRecovery'
import type { DashboardCurrentSnapshot } from '../types/dashboardCurrent'
import type {
  Position,
  PositionCreate,
  PositionUpdate,
  PriceObservation,
  PositionClose,
  PositionEnhanced,
  PositionEnhancedList,
  DashboardMetrics,
  PortfolioValuation,
  CSVImportResponse,
  ConfirmImportRequest,
  ConfirmImportResponse,
  APIError,
  CashLedgerEntry,
  CashLedgerEntryCreate,
  CashLedgerSummary,
} from '../types/position'
import type {
  Portfolio,
  PortfolioCreate,
  PortfolioUpdate,
  PortfolioList,
  PortfolioWithMetrics,
  PortfolioDeleteResponse,
  PortfolioTimeframePerformance,
} from '../types/portfolio'
import type {
  Strategy,
  StrategyCreate,
  StrategyUpdate,
  StrategyList,
  StrategyWithLegs,
  StrategyWithLegsList,
  StrategyLeg,
  StrategyLegCreate,
  StrategyMetadata,
  StrategyType,
  StrategyStatus,
  StrategyValidationResult,
} from '../types/strategy'
import type { Timeframe } from '../utils/timeframe'
import type { EdgePeriod, EdgeReport } from '../types/edge'
import type {
  PerformanceCalendarResponse,
  PerformanceClosingPriceSubmission,
} from '../types/performanceCalendar'
import type { AssetFilter } from '../types/position'
import type {
  OrderIntent,
  OrderIntentRequest,
  Scenario,
  ScenarioAnalysis,
  ScenarioAnalyzeRequest,
  ScenarioCreate,
  ScenarioList,
  ScenarioTracking,
  ScenarioTrackingObservation,
  ScenarioTrackingObservationPage,
  ScenarioTrackingCapabilities,
  ScenarioTrackingDiagnostics,
  ScenarioUpdate,
} from '../types/scenarioLab'

const API_URL = API_BASE_URL

// Dev mode auth bypass - use mock token instead of real Supabase session
const DEV_AUTH_BYPASS =
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'test' &&
  import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

export interface BrokerSyncConnection {
  id: string
  provider: 'ibkr' | 'tastytrade' | 'snaptrade'
  status: string
  account_count: number
  last_sync_status: string | null
  needs_reauth: boolean
  last_successful_sync_at: string | null
  mismatch_count: number
}

export interface BrokerSyncAccount {
  id: string
  broker_connection_id: string
  provider: 'ibkr' | 'tastytrade' | 'snaptrade'
  provider_account_id: string
  account_name: string | null
  status: string
  portfolio_id: string | null
  mismatch_count: number
  needs_review: boolean
  ibkr_transactions_enabled: boolean | null
  ibkr_transaction_conids: number[] | null
  ibkr_transaction_currency: string | null
  ibkr_transaction_days: number | null
}

export interface BrokerSyncConnectionsResponse {
  connections: BrokerSyncConnection[]
}

export interface BrokerSyncAccountsResponse {
  accounts: BrokerSyncAccount[]
}

export type CustomerBrokerSyncEntitlement = 'allowed' | 'locked' | 'paused'
export type CustomerBrokerSyncConnectionStatus =
  | 'not_connected'
  | 'pending'
  | 'healthy'
  | 'syncing'
  | 'reconnect_required'
  | 'paused'
  | 'failed'

export interface CustomerBrokerPortfolio {
  id: string
  name: string
}

export interface CustomerBrokerPortfolioOption extends CustomerBrokerPortfolio {
  hasData: boolean
  brokerSyncOwned: boolean
  brokerAccountCount: number
}

export interface CustomerBrokerReviewItem {
  occurredAt: string
  symbol: string | null
  activityType: string
  status: 'not_imported'
  message: string
}

export interface CustomerBrokerImportSummary {
  totalActivities: number | null
  importableTransactions: number | null
  importedCount: number | null
  needsReviewCount: number
  reviewItems?: CustomerBrokerReviewItem[]
}

export interface CustomerBrokerConnection {
  id: string
  brokerName: string
  status: CustomerBrokerSyncConnectionStatus
  accountCount: number
  lastSuccessfulSyncAt: string | null
  safeMessage: string
  canReconnect: boolean
  canRemove: boolean
  providerDeletionStatus: 'not_requested' | 'pending' | 'success' | 'failed' | 'admin_required'
}

export interface CustomerBrokerAccount {
  id: string | null
  connectionId: string | null
  brokerage: string
  accountName: string
  maskedAccountNumber: string
  currency: string
  status: string
  mappingStatus: 'needs_configuration' | 'configured'
  linkedPortfolio: CustomerBrokerPortfolio | null
  linkedPortfolioHasData: boolean
  defaultPortfolioName: string
  importMode: 'import_from_date' | 'snapshot_baseline_future' | null
  importStartDate: string | null
  assumeNoPriorOptionPositions: boolean
  importStatus:
    | 'pending'
    | 'preview_ready'
    | 'importing'
    | 'completed'
    | 'completed_with_review_items'
    | 'failed'
    | 'blocked'
  lastImportPreviewAt: string | null
  lastImportCommitAt: string | null
  hasImportedHistory: boolean
  safeMessage: string
  importSummary?: CustomerBrokerImportSummary | null
  canConfigure: boolean
}

export interface CustomerBrokerSyncStatus {
  entitlement: CustomerBrokerSyncEntitlement
  connectionStatus: CustomerBrokerSyncConnectionStatus
  connections: CustomerBrokerConnection[]
  brokerages: string[]
  accounts: CustomerBrokerAccount[]
  linkedPortfolios: CustomerBrokerPortfolio[]
  availablePortfolios: CustomerBrokerPortfolioOption[]
  lastSuccessfulSyncAt: string | null
  lastAttemptedSyncAt: string | null
  nextScheduledSyncAt: string | null
  safeMessage: string
  canConnect: boolean
  canRefresh: boolean
  canReconnect: boolean
  canDisconnect: boolean
  canAddConnection: boolean
  canRemoveConnection: boolean
  canMapPortfolio: boolean
  canImportFromDate: boolean
}

export interface CustomerBrokerPortalResponse {
  url: string
  expiresInSeconds: number
  message?: string | null
}

export type BrokerSyncPortalClient = 'web' | 'native'

export interface CustomerBrokerRefreshResponse {
  status: 'success' | 'running' | 'pending' | 'skipped'
  alreadyUpToDate: boolean
  message: string
  skipReason?: string | null
  brokerSync: CustomerBrokerSyncStatus
}

export interface CustomerBrokerImportPreview {
  accountId: string
  status: 'preview_ready' | 'blocked'
  message: string
  totalActivitiesFound: number
  importableStockTransactionsCount: number
  importableOptionTransactionsCount: number
  cashMovementsCount: number
  dividendsFeesCount: number
  skippedCount: number
  matchedExistingCount: number
  conflictCount: number
  needsReviewCount: number
  dateRange: { from: string; to: string } | null
  warnings: string[]
  reviewItems?: CustomerBrokerReviewItem[]
}

export interface CustomerBrokerImportCommit {
  accountId: string
  status: 'completed' | 'completed_with_review_items'
  message: string
  importedCount: number
  postedCount: number
  skippedCount: number
  needsReviewCount: number
  reconciliationWarningCount: number
  warnings: string[]
  reviewItems?: CustomerBrokerReviewItem[]
}

export interface BrokerSyncReconnectResponse {
  connection_id: string
  status: string
  sync_result?: BrokerSyncRunResponse | null
}

export interface BrokerSyncRunResponse {
  run_id: string | null
  status: string
  account_count: number | null
  raw_event_count: number
  normalized_event_count: number
  applied_event_count: number
  position_bootstrap_event_count: number
  mismatch_count: number
  blocked_accounts: number
  accounts_discovered: number
}

export interface BrokerSyncLinkResult {
  connection_id: string
  status: string
  accounts_created: number
  bootstrap?: {
    status?: string
    raw_event_count?: number
    normalized_event_count?: number
    applied_event_count?: number
    mismatch_count?: number
  } | null
}

export interface IbkrTransactionSettingsUpdateRequest {
  enabled: boolean
  conids: number[]
  currency?: string
  days?: number
}

export interface IbkrTransactionSettingsUpdateResponse {
  account_id: string
  ibkr_transactions_enabled: boolean
  ibkr_transaction_conids: number[]
  ibkr_transaction_currency: string
  ibkr_transaction_days: number
}

export interface AdminSnapTradeStatus {
  configured: boolean
  environment: string
  hasSnapTradeUser: boolean
  connectionCount: number
  accountCount: number
  lastSyncRun: AdminSnapTradeSyncRun | null
  warnings: string[]
  reconnectRequired?: boolean
  credentialScope?: {
    current?: AdminSnapTradeCredentialScope
    stored?: AdminSnapTradeCredentialScope | Record<string, never>
    matches?: boolean
    mismatch?: boolean
    reconnectRequired?: boolean
  }
}

export interface AdminSnapTradeCredentialScope {
  version?: number
  environment?: string
  base_url?: string
  client_id_hash?: string | null
  scope_label?: string | null
}

export interface AdminSnapTradeConnection {
  id: string
  provider_connection_id: string | null
  display_name: string | null
  status: string
  metadata?: {
    snaptrade_type?: string
    disabled?: boolean
    brokerage?: {
      slug?: string
      name?: string
      display_name?: string
    }
    read_only_warning?: string
    snaptrade_sync_warning?: string
    snaptrade_pending_accounts_at?: string
  }
  last_synced_at: string | null
  last_successful_sync_at: string | null
  created_at: string
}

export interface AdminSnapTradeAccount {
  id: string
  broker_connection_id: string
  portfolio_id: string | null
  provider_account_id: string
  institution_name: string | null
  account_number_mask: string | null
  account_type: string | null
  account_name: string | null
  base_currency: string | null
  status: string
  metadata?: {
    snaptrade_authorization_id?: string
    import_mode?: string
    import_start_date?: string | null
    historical_option_prior_state?: string | null
    auto_import_enabled?: boolean
    auto_post_high_confidence_events?: boolean
    require_review_for_options?: boolean
    balance?: {
      total?: {
        amount?: number
        currency?: string
      }
    }
  }
  created_at: string
  updated_at: string
}

export interface AdminSnapTradeBrokerEvent {
  id: string
  broker_account_id: string
  provider_event_id: string | null
  external_order_id: string | null
  deterministic_hash: string | null
  event_type: string
  raw_activity_type: string | null
  asset_type: string | null
  symbol: string | null
  option_underlying: string | null
  option_type: string | null
  strike: number | null
  expiration: string | null
  quantity: number | null
  price: number | null
  gross_amount: number | null
  net_amount: number | null
  fees: number | null
  commission: number | null
  currency: string | null
  action: string | null
  source_status: 'imported' | 'matched' | 'ignored' | 'needs_review' | 'posted' | 'error'
  confidence: 'high' | 'medium' | 'low'
  review_reason: string | null
  occurred_at: string
  created_at?: string | null
  updated_at?: string | null
  posted_at: string | null
  can_post?: boolean
  post_label?: string | null
  post_kind?: 'broker_event' | 'snapshot_baseline' | string | null
  post_warning?: string | null
  post_disabled_reason?: string | null
  metadata?: Record<string, unknown>
}

export interface AdminSnapTradeReconciliationWarning {
  id: string
  broker_account_id: string
  symbol: string | null
  option_underlying: string | null
  option_type: string | null
  strike: number | null
  expiration: string | null
  expected_quantity: number | null
  actual_quantity: number | null
  mismatch_type: string
  details?: Record<string, unknown>
  status: string
  created_at?: string
  detected_at?: string
  last_seen_at?: string
  resolved_at?: string | null
}

export interface AdminSnapTradePosition {
  id: string
  snaptrade_account_id: string
  symbol: string
  underlying_symbol: string | null
  asset_type: string
  quantity: number | null
  average_price: number | null
  market_price: number | null
  market_value: number | null
  currency: string | null
  as_of: string
}

export interface AdminSnapTradeOptionPosition {
  id: string
  snaptrade_account_id: string
  option_symbol: string
  underlying_symbol: string | null
  contract_type: 'call' | 'put' | 'unknown'
  strike_price: number | null
  expiration_date: string | null
  quantity: number | null
  average_price: number | null
  market_price: number | null
  market_value: number | null
  currency: string | null
  as_of: string
}

export interface AdminSnapTradeActivity {
  id: string
  snaptrade_account_id: string
  external_activity_id: string | null
  trade_date: string | null
  settlement_date: string | null
  activity_type: string
  symbol: string | null
  underlying_symbol: string | null
  option_symbol: string | null
  description: string | null
  quantity: number | null
  price: number | null
  amount: number | null
  fees: number | null
  currency: string | null
}

export interface AdminSnapTradeSyncRun {
  id: string
  status: 'started' | 'success' | 'partial' | 'failed'
  started_at: string
  completed_at: string | null
  connections_count: number
  accounts_count: number
  positions_count: number
  option_positions_count: number
  activities_count: number
  error_message: string | null
  warnings_json: string[] | null
}

export interface AdminBrokerSyncSupportUser {
  user_id: string
  email: string
  subscription_status: string
  is_premium: boolean
  last_seen_at: string | null
  status:
    | 'active'
    | 'syncing'
    | 'failed'
    | 'reconnect_required'
    | 'paused_due_to_subscription'
    | 'pending_accounts'
  has_snaptrade_user: boolean
  connected_brokers: string[]
  connection_ids: string[]
  connection_count: number
  sync_paused: boolean
  account_count: number
  last_successful_sync_at: string | null
  last_attempted_sync_at: string | null
  latest_sync_run: Record<string, unknown> | null
  sync_run_count: number
  last_error: string | null
  raw_activity_count: number
  imported_event_count: number
  posted_event_count: number
  reconciliation_warning_count: number
  account_configurations: Array<{
    id: string
    status: string
    institution_name?: string | null
    account_name?: string | null
    portfolio_id?: string | null
    configuration_status?: string | null
    mapping_source?: string | null
    customer_import_mode?: string | null
    customer_import_start_date?: string | null
    assume_no_prior_option_positions?: boolean
    import_status?: string | null
    last_import_preview_at?: string | null
    last_import_commit_at?: string | null
    last_customer_action_at?: string | null
    last_customer_message_safe?: string | null
  }>
  latest_customer_import_action: {
    action?: string
    status?: string
    safe_message?: string | null
    safe_summary?: Record<string, unknown> | null
    internal_summary?: Record<string, unknown> | null
    created_at?: string | null
  } | null
  latest_historical_plan: {
    import_mode?: string | null
    import_start_date?: string | null
    status?: string | null
    activity_count?: number
    postable_event_count?: number
    needs_review_event_count?: number
    blocked_event_count?: number
    created_at?: string | null
  } | null
  provider_deletions: Array<{
    connection_id: string
    status?: string | null
    retry_count?: number
    requested_at?: string | null
    last_attempted_at?: string | null
    last_error?: string | null
    deleted_at?: string | null
  }>
}

export interface AdminSnapTradeImportPreview {
  positions: AdminSnapTradePosition[]
  optionPositions: AdminSnapTradeOptionPosition[]
  activities: AdminSnapTradeActivity[]
  brokerEvents: AdminSnapTradeBrokerEvent[]
  needsReviewEvents: AdminSnapTradeBrokerEvent[]
  postedEvents: AdminSnapTradeBrokerEvent[]
  reconciliationWarnings: AdminSnapTradeReconciliationWarning[]
  syncRuns: AdminSnapTradeSyncRun[]
}

export interface AdminSnapTradeHistoricalPlanEvent {
  order: number
  id: string | null
  occurred_at: string
  source_time_missing?: boolean
  occurred_at_semantics?: string | null
  provider_event_id: string | null
  normalized_event_key: string
  event_type: string
  raw_activity_type: string | null
  asset_type: string | null
  symbol: string | null
  option_underlying: string | null
  option_type: string | null
  strike: number | null
  expiration: string | null
  quantity: number | null
  price: number | null
  net_amount: number | null
  currency: string | null
  confidence: string
  source_status: string
  review_state: string | null
  plan_status: string
  plan_reason: string
  planned_event_type?: string | null
  inference_reason?: string | null
  portfolio_tabs: string[]
  auto_post_eligible: boolean
}

export interface AdminSnapTradeHistoricalPlan {
  plan_id: string | null
  generated_at: string
  provider: string
  broker_account_id: string
  broker_connection_id: string
  portfolio_id: string | null
  import_mode: string
  import_start_date: string | null
  option_prior_state?: string | null
  counts: Record<string, number>
  event_type_breakdown: Record<string, number>
  confidence_breakdown: Record<string, number>
  source_status_breakdown: Record<string, number>
  decision_breakdown: Record<string, number>
  portfolio_impact: Record<string, number>
  reconciliation_preview: Record<string, unknown>
  apply_allowed: boolean
  assumptions?: Array<Record<string, unknown>>
  blockers: Array<Record<string, unknown>>
  warnings: Array<Record<string, unknown>>
  events: AdminSnapTradeHistoricalPlanEvent[]
  events_capped: boolean
  total_event_count: number
}

export interface AdminSnapTradeSyncResult {
  runId: string
  status: string
  connectionsCount: number
  accountsCount: number
  positionsImported: number
  optionPositionsImported: number
  activitiesImported: number
  brokerSyncAccounts: number
  brokerSyncAppliedEvents: number
  portfolioImports: number
  warnings: string[]
  errors: string[]
}

export interface EmailPreferences {
  onboarding_emails: boolean
  product_updates_emails: boolean
  marketing_emails: boolean
  unsubscribed_all_nonessential: boolean
  essential_account_emails: boolean
}

export type EmailPreferencesUpdate = Partial<
  Pick<
    EmailPreferences,
    | 'onboarding_emails'
    | 'product_updates_emails'
    | 'marketing_emails'
    | 'unsubscribed_all_nonessential'
  >
>

// ============================================================================
// Helper Functions
// ============================================================================

const FIRST_POSITION_ANALYTICS_KEY = 'optioneer_analytics_first_position_added'
const FIRST_OPTION_POSITION_ANALYTICS_KEY = 'optioneer_analytics_first_option_position_added'

interface PositionCountSnapshot {
  totalPositions: number
  optionPositions: number
}

export class ApiClientError extends Error {
  status: number
  detail: unknown
  code?: string

  constructor(message: string, status: number, detail?: unknown) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.detail = detail

    if (
      detail &&
      typeof detail === 'object' &&
      'code' in detail &&
      typeof (detail as { code?: unknown }).code === 'string'
    ) {
      this.code = (detail as { code: string }).code
    }
  }
}

function getAnalyticsFlag(key: string): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(key) === 'true'
}

function setAnalyticsFlag(key: string): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(key, 'true')
}

async function getPositionCountSnapshot(): Promise<PositionCountSnapshot | null> {
  try {
    const [optionsResult, stocksResult] = await Promise.allSettled([
      getPositions({ include_market_data: false, limit: 1 }),
      getUnderlyingPositions(undefined, undefined, true),
    ])

    const optionPositions =
      optionsResult.status === 'fulfilled' ? Number(optionsResult.value.total || 0) : 0
    const stockPositions =
      stocksResult.status === 'fulfilled' ? Number(stocksResult.value.total_count || 0) : 0

    return {
      totalPositions: optionPositions + stockPositions,
      optionPositions,
    }
  } catch {
    return null
  }
}

function trackPositionActivationAfterCreate(
  snapshot: PositionCountSnapshot | null,
  source: 'manual_option' | 'manual_stock' | 'csv_import',
  importedOptions = 0
): void {
  if (!snapshot) return

  if (snapshot.totalPositions === 0 && !getAnalyticsFlag(FIRST_POSITION_ANALYTICS_KEY)) {
    const tracked = trackDataFastActivationEvent('first_position_added', { source })
    if (tracked) setAnalyticsFlag(FIRST_POSITION_ANALYTICS_KEY)
  }

  if (
    importedOptions > 0 &&
    snapshot.optionPositions === 0 &&
    !getAnalyticsFlag(FIRST_OPTION_POSITION_ANALYTICS_KEY)
  ) {
    const tracked = trackDataFastActivationEvent('first_option_position_added', { source })
    if (tracked) setAnalyticsFlag(FIRST_OPTION_POSITION_ANALYTICS_KEY)
  }
}

/**
 * Get authentication headers with current user's JWT token
 * In DEV_AUTH_BYPASS mode, uses mock dev-access-token instead of Supabase session
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  // In dev bypass mode, use the mock token that the backend expects
  if (DEV_AUTH_BYPASS) {
    return {
      'Content-Type': 'application/json',
      Authorization: 'Bearer dev-access-token',
    }
  }

  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  }
}

/**
 * Handle API response and throw errors if needed
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API error: ${response.status} ${response.statusText}`
    let detail: unknown

    try {
      const errorData: APIError = await response.json()
      detail = errorData.detail ?? errorData.error
      // If detail is an object/array, stringify it so we don't get [object Object]
      if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        errorMessage =
          typeof (errorData.detail as { message?: unknown }).message === 'string'
            ? (errorData.detail as { message: string }).message
            : JSON.stringify(errorData.detail)
      } else if (errorData.error) {
        errorMessage = errorData.error
      } else {
        errorMessage = typeof errorData.detail === 'string' ? errorData.detail : errorMessage
      }
    } catch {
      // If JSON parsing fails, use default error message
    }

    const error = new ApiClientError(errorMessage, response.status, detail)
    if (response.status === 403 && error.code === 'PAYMENT_REQUIRED') {
      window.dispatchEvent(new Event(PAYMENT_REQUIRED_EVENT))
    }
    throw error
  }

  // Handle 204 No Content responses (e.g., DELETE operations)
  if (response.status === 204) {
    return { success: true, message: 'Operation completed successfully' } as T
  }

  return response.json()
}

/**
 * Pending profile/entitlement read shared by active consumers only.
 */
interface PendingAuthRead {
  controller: AbortController
  consumers: number
  settled: boolean
  response: Promise<unknown>
}

// Share only overlapping profile/entitlement reads. Completed responses are
// never cached, and a changed session token always starts a separate request.
const pendingAuthReads = new Map<string, PendingAuthRead>()

function sharedAuthRead<T>(
  key: string,
  request: (signal: AbortSignal) => Promise<T>,
  signal?: AbortSignal | null
): Promise<T> {
  if (signal?.aborted) {
    return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
  }

  let pending = pendingAuthReads.get(key)
  if (!pending) {
    const controller = new AbortController()
    pending = {
      controller,
      consumers: 0,
      settled: false,
      response: Promise.resolve().then(() => request(controller.signal)),
    }
    pendingAuthReads.set(key, pending)
    const current = pending
    const remove = () => {
      current.settled = true
      if (pendingAuthReads.get(key) === current) pendingAuthReads.delete(key)
    }
    void current.response.then(remove, remove)
  }

  const current = pending
  current.consumers += 1
  return new Promise<T>((resolve, reject) => {
    let finished = false
    const finish = (complete: () => void) => {
      if (finished) return
      finished = true
      signal?.removeEventListener('abort', abort)
      current.consumers -= 1
      if (!current.settled && current.consumers === 0) {
        if (pendingAuthReads.get(key) === current) pendingAuthReads.delete(key)
        current.controller.abort()
      }
      complete()
    }
    const abort = () =>
      finish(() => reject(signal?.reason ?? new DOMException('Aborted', 'AbortError')))
    signal?.addEventListener('abort', abort, { once: true })
    void current.response.then(
      (value) => finish(() => resolve(value as T)),
      (error: unknown) => finish(() => reject(error))
    )
  })
}

/** Make an authenticated GET request, with optional sharing for startup reads. */
async function get<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean>,
  init?: RequestInit,
  shareAuthRead = false
): Promise<T> {
  const headers = await getAuthHeaders()
  const url = new URL(`${API_URL}${endpoint}`)

  // Add query parameters, filtering out undefined/null values
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  const request = async (signal?: AbortSignal | null) =>
    handleResponse<T>(
      await fetch(url.toString(), {
        method: 'GET',
        headers,
        ...init,
        ...(signal && { signal }),
      })
    )
  if (shareAuthRead && Object.keys(init ?? {}).every((key) => key === 'signal')) {
    const authorization = new Headers(headers).get('Authorization')
    if (authorization) {
      return sharedAuthRead(`${url}\n${authorization}`, request, init?.signal)
    }
  }
  return request()
}

/**
 * Make authenticated POST request
 */
async function post<T>(endpoint: string, data?: unknown, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined,
    ...init,
  })

  return handleResponse<T>(response)
}

/**
 * Make authenticated PUT request
 */
async function put<T>(endpoint: string, data: unknown, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
    ...init,
  })

  return handleResponse<T>(response)
}

/**
 * Make authenticated PATCH request
 */
async function patch<T>(endpoint: string, data: unknown, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
    ...init,
  })

  return handleResponse<T>(response)
}

/**
 * Make authenticated DELETE request
 */
async function del<T>(endpoint: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders()

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'DELETE',
    headers,
    ...init,
  })

  return handleResponse<T>(response)
}

/**
 * Make authenticated POST request with FormData (for file uploads)
 */
async function postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  let token: string | undefined

  // In dev bypass mode, use the mock token
  if (DEV_AUTH_BYPASS) {
    token = 'dev-access-token'
  } else {
    const { data } = await supabase.auth.getSession()
    token = data.session?.access_token
  }

  const headers: HeadersInit = {
    ...(token && { Authorization: `Bearer ${token}` }),
    // Note: Don't set Content-Type for FormData, browser will set it with boundary
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  })

  return handleResponse<T>(response)
}

// ============================================================================
// Position API Methods
// ============================================================================

/**
 * Get all positions with optional filters and market data
 *
 * @param params - Query parameters for filtering and pagination
 * @returns List of enhanced positions with metrics
 */
export async function getPositions(params?: {
  portfolio_id?: string
  all_portfolios?: boolean
  status?: 'open' | 'closed'
  include_market_data?: boolean
  skip?: number
  limit?: number
}): Promise<PositionEnhancedList> {
  return get<PositionEnhancedList>(
    '/api/positions',
    params as Record<string, string | number | boolean>
  )
}

/**
 * Get a specific position by ID
 *
 * @param id - Position UUID
 * @param includeMarketData - Whether to fetch current market data
 * @returns Enhanced position with metrics
 */
export async function getPosition(id: string, includeMarketData = true): Promise<PositionEnhanced> {
  return get<PositionEnhanced>(`/api/positions/${id}`, {
    include_market_data: includeMarketData,
  })
}

export interface PositionContractMetadataRepairResult {
  position_id: string
  status: 'repaired' | 'unchanged' | 'unresolved' | 'failed'
  option_symbol?: string | null
  updated_fields: string[]
  reason?: string | null
}

export interface PositionContractMetadataRepairResponse {
  results: PositionContractMetadataRepairResult[]
  repaired_count: number
  unchanged_count: number
  unresolved_count: number
  failed_count: number
}

/** Persist only provider-validated option contract metadata for selected positions. */
export async function repairPositionContractMetadata(request: {
  portfolio_id: string
  position_ids: string[]
}): Promise<PositionContractMetadataRepairResponse> {
  return post<PositionContractMetadataRepairResponse>(
    '/api/positions/contract-metadata/repair',
    request
  )
}

/** Get lifecycle-aware realized performance for the Your Edge page. */
export async function getEdgeReport(params: {
  period: EdgePeriod
  portfolio_id?: string
}): Promise<EdgeReport> {
  return get<EdgeReport>('/api/edge', params as Record<string, string>)
}

/**
 * Create a new position
 *
 * @param position - Position data to create
 * @returns Created position
 */
export async function createPosition(position: PositionCreate): Promise<Position> {
  const needsSnapshot =
    !getAnalyticsFlag(FIRST_POSITION_ANALYTICS_KEY) ||
    !getAnalyticsFlag(FIRST_OPTION_POSITION_ANALYTICS_KEY)
  const snapshot = needsSnapshot ? await getPositionCountSnapshot() : null
  const created = await post<Position>('/api/positions', position)
  trackPositionActivationAfterCreate(snapshot, 'manual_option', 1)
  return created
}

/**
 * Update an existing position
 *
 * @param id - Position UUID
 * @param updates - Partial position data to update
 * @returns Updated position
 */
export async function updatePosition(id: string, updates: PositionUpdate): Promise<Position> {
  return put<Position>(`/api/positions/${id}`, updates)
}

/**
 * Close an open position
 *
 * @param id - Position UUID
 * @param closeData - Exit premium/date and optional quantity for partial close
 * @returns Updated position with closed status
 */
export async function closePosition(id: string, closeData: PositionClose): Promise<Position> {
  return post<Position>(`/api/positions/${id}/close`, closeData)
}

/**
 * Manually update current premium for an open position
 *
 * @param id - Position UUID
 * @param currentPremium - Current premium per share
 * @param priceObservation - Explicit observation date and source
 * @returns Updated position with new current_premium
 */
export async function updatePositionPrice(
  id: string,
  currentPremium: number,
  priceObservation: PriceObservation
): Promise<Position> {
  return put<Position>(`/api/positions/${id}/update-price`, {
    current_premium: currentPremium,
    price_observation: priceObservation,
  })
}

/**
 * Delete a position
 *
 * @param id - Position UUID
 * @returns Success response
 */
export async function deletePosition(id: string): Promise<{ success: boolean; message: string }> {
  return del<{ success: boolean; message: string }>(`/api/positions/${id}`)
}

/**
 * Delete ALL positions for current user (destructive operation)
 *
 * @returns Object with deleted count and message
 */
export async function deleteAllPositions(): Promise<{ deleted_count: number; message: string }> {
  return del<{ deleted_count: number; message: string }>('/api/positions/all')
}

/**
 * Delete only OPEN positions for current user
 *
 * @param portfolioId - Optional portfolio ID to filter deletion. If not provided, deletes from ALL portfolios.
 * @returns Object with deleted count and message
 */
export async function deleteAllOpenPositions(portfolioId?: string): Promise<{
  deleted_count: number
  message: string
}> {
  const params = portfolioId ? `?portfolio_id=${portfolioId}` : ''
  return del<{ deleted_count: number; message: string }>(`/api/positions/all/open${params}`)
}

/**
 * Delete only CLOSED positions (trade history) for current user
 *
 * @param portfolioId - Optional portfolio ID to filter deletion. If not provided, deletes from ALL portfolios.
 * @returns Object with deleted count and message
 */
export async function deleteAllClosedPositions(portfolioId?: string): Promise<{
  deleted_count: number
  message: string
}> {
  const params = portfolioId ? `?portfolio_id=${portfolioId}` : ''
  return del<{ deleted_count: number; message: string }>(`/api/positions/all/closed${params}`)
}

// ============================================================================
// Portfolio API Methods
// ============================================================================

export async function getPerformanceCalendar(
  params: { month: string; asset_filter: AssetFilter; portfolio_id?: string },
  signal?: AbortSignal
): Promise<PerformanceCalendarResponse> {
  return get<PerformanceCalendarResponse>(
    '/api/performance/calendar',
    { ...params, contract: 'nav-flow-v4' },
    { signal }
  )
}

export async function addPerformanceClosingPrice(input: PerformanceClosingPriceSubmission) {
  return post<{ status: 'saved'; closing_date: string }>('/api/performance/closing-prices', input)
}

async function getCSVDownload(
  path: string,
  params: Record<string, string | undefined>
): Promise<Blob> {
  const headers = await getAuthHeaders()
  const url = new URL(`${API_URL}${path}`)
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) url.searchParams.set(key, value)
  })
  const controller = new AbortController()
  let timedOut = false
  const timeout = window.setTimeout(() => {
    timedOut = true
    controller.abort()
  }, 30_000)
  try {
    const response = await fetch(url, { headers, signal: controller.signal })
    if (!response.ok) {
      await handleResponse<never>(response)
    }
    return await response.blob()
  } catch (error) {
    if (timedOut) throw new Error('CSV export took too long. Try again.')
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

export async function exportPerformanceCalendar(params: {
  month: string
  asset_filter: AssetFilter
  portfolio_id?: string
}): Promise<Blob> {
  return getCSVDownload('/api/performance/calendar/export.csv', {
    ...params,
    contract: 'nav-flow-v4',
  })
}

/**
 * Get all portfolios for the current user
 *
 * @returns List of portfolios with default portfolio ID
 */
export async function getPortfolios(): Promise<PortfolioList> {
  return get<PortfolioList>('/api/portfolios')
}

/**
 * Get a specific portfolio by ID with metrics
 *
 * @param id - Portfolio UUID
 * @returns Portfolio with metrics
 */
export async function getPortfolio(id: string): Promise<PortfolioWithMetrics> {
  return get<PortfolioWithMetrics>(`/api/portfolios/${id}`)
}

/**
 * Get portfolio performance for a selected timeframe
 *
 * @param id - Portfolio UUID
 * @param timeframe - Timeframe code (D, M, 3M, 6M, YTD, ALL)
 */
export async function getPortfolioTimeframePerformance(
  id: string,
  timeframe: Timeframe,
  signal?: AbortSignal
): Promise<PortfolioTimeframePerformance> {
  return get<PortfolioTimeframePerformance>(
    `/api/portfolios/${id}/performance`,
    { timeframe },
    signal ? { signal } : undefined
  )
}

/**
 * Create a new portfolio
 *
 * @param portfolio - Portfolio data to create
 * @returns Created portfolio
 */
export async function createPortfolio(portfolio: PortfolioCreate): Promise<Portfolio> {
  const created = await post<Portfolio>('/api/portfolios', portfolio)
  trackDataFastActivationEvent('portfolio_created', { source: 'manual_create' })
  return created
}

/**
 * Create a new demo portfolio
 *
 * @returns Created demo portfolio
 */
export async function createDemoPortfolio(): Promise<Portfolio> {
  return post<Portfolio>('/api/portfolios/demo')
}

/**
 * Update an existing portfolio
 *
 * @param id - Portfolio UUID
 * @param updates - Partial portfolio data to update
 * @returns Updated portfolio
 */
export async function updatePortfolio(id: string, updates: PortfolioUpdate): Promise<Portfolio> {
  return put<Portfolio>(`/api/portfolios/${id}`, updates)
}

/**
 * Delete a portfolio
 *
 * @param id - Portfolio UUID
 * @param force - If true, delete even if portfolio has positions
 * @returns Delete response
 */
export async function deletePortfolio(
  id: string,
  force: boolean = false
): Promise<PortfolioDeleteResponse> {
  return del<PortfolioDeleteResponse>(`/api/portfolios/${id}?force=${force}`)
}

/**
 * Set a portfolio as the user's default
 *
 * @param id - Portfolio UUID
 * @returns Updated portfolio
 */
export async function setDefaultPortfolio(id: string): Promise<Portfolio> {
  return post<Portfolio>(`/api/portfolios/${id}/set-default`)
}

// ============================================================================
// Dashboard API Methods
// ============================================================================

/**
 * Read an owned current snapshot. Accounting remains included by default for
 * existing callers; the web's quote-only views explicitly opt out. Modes have
 * distinct request URLs and this transport never caches or shares their results.
 */
export async function getDashboardCurrent(
  scope: string,
  signal?: AbortSignal,
  options: { includeAccounting?: boolean } = {}
): Promise<DashboardCurrentSnapshot> {
  return get<DashboardCurrentSnapshot>(
    '/api/dashboard/current',
    { scope, include_accounting: options.includeAccounting ?? true },
    signal ? { signal } : undefined
  )
}

/** Get comprehensive historical metrics for the optional portfolio filter. */
export async function getDashboardMetrics(
  portfolioId?: string,
  signal?: AbortSignal
): Promise<DashboardMetrics> {
  const params = portfolioId ? { portfolio_id: portfolioId } : undefined
  return get<DashboardMetrics>(
    '/api/dashboard/metrics',
    params as Record<string, string>,
    signal ? { signal } : undefined
  )
}

/**
 * Get historical P&L data for chart (v2 - uses database snapshots)
 *
 * @param days Number of days to look back (default 30)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Historical P&L data with realized, unrealized, and total arrays
 */
export async function getHistoricalPnl(
  days: number = 30,
  signal?: AbortSignal
): Promise<{
  realized: Array<{ date: string; pnl: number; label: string }>
  unrealized: Array<{ date: string; pnl: number; label: string }>
  total: Array<{ date: string; pnl: number; label: string }>
}> {
  return get(
    `/api/dashboard/historical-pnl-v2?days=${days}`,
    undefined,
    signal ? { signal } : undefined
  )
}

/**
 * Get hybrid P&L data (v3 - events + snapshots)
 *
 * @param days Number of days to look back (default 30)
 * @param portfolioId - Optional portfolio ID to filter by
 * @param signal - Optional AbortSignal for cancellation
 * @returns Hybrid P&L data with events, snapshots, and tracking metadata
 */
export async function getHistoricalPnlV3(
  days: number = 30,
  portfolioId?: string,
  signal?: AbortSignal
): Promise<{
  realized: {
    events: Array<{
      date: string
      pnl: number
      type: 'exit'
      position_id: string
      ticker: string
    }>
    cumulative: Array<{ date: string; pnl: number; label: string }>
  }
  unrealized: {
    events: Array<{
      date: string
      pnl: number
      type: 'entry'
      position_id: string
      ticker: string
      tracked_from: string | null
    }>
    snapshots: Array<{ date: string; pnl: number; label: string; is_tracked: boolean }>
    current: { date: string; pnl: number; label: string } | null
  }
  stockPnl: {
    snapshots: Array<{
      date: string
      unrealized: number
      realized: number
      total: number
      label: string
    }>
    current: {
      date: string
      unrealized: number
      realized: number
      total: number
      label: string
    } | null
  }
  dailyHoldingPnl?: Record<
    string,
    {
      stocks: number
      options: number
      total: number
      stocksAllTimePnl?: number
      optionsAllTimePnl?: number
      allTimePnl?: number
      stocksPct: number | null
      optionsPct: number | null
      totalPct: number | null
      stocksCurrentValue: number
      optionsCurrentValue: number
      currentValue: number
      stocksBaselineUnavailableCount?: number
      optionsBaselineUnavailableCount?: number
      stocksMovementAvailable?: boolean
      optionsMovementAvailable?: boolean
      stocksMovementDate?: string | null
      optionsMovementDate?: string | null
      stocksCurrentPriceUnavailableCount?: number
      optionsCurrentPriceUnavailableCount?: number
      stockDebug?: {
        currentPrice: number | null
        previousClose: number | null
        previousCloseDate: string | null
        /** Actual comparison price date; null means no single dated reference. */
        comparisonPriceDate?: string | null
        currentPriceDate: string | null
        currentPriceSource: string | null
        quantityAbs: number
        positionsCount: number
      }
      optionDebug?: {
        currentPremium: number | null
        previousClose: number | null
        previousCloseDate: string | null
        /** Actual comparison price date; null means no single dated reference. */
        comparisonPriceDate?: string | null
        currentPremiumDate: string | null
        currentPremiumSource: string | null
        contractsAbs: number
        positionsCount: number
        symbols?: string[]
        legs?: Array<{
          symbol: string
          contracts: number
          currentPremium: number
          previousClose: number | null
          previousCloseDate: string | null
          /** Actual comparison price date; null means no single dated reference. */
          comparisonPriceDate?: string | null
          referenceDate: string | null
          source: string | null
          todayPnl: number
        }>
      }
    }
  >
  valuation?: PortfolioValuation
  tracking_metadata: {
    first_snapshot_date: string | null
    earliest_entry_date: string | null
    has_pre_tracking_data: boolean
  }
}> {
  const params = new URLSearchParams({ days: days.toString() })
  if (portfolioId) params.append('portfolio_id', portfolioId)
  return get(
    `/api/dashboard/historical-pnl-v3?${params.toString()}`,
    undefined,
    signal ? { signal } : undefined
  )
}

/**
 * Aggregate response type for all portfolios dashboard
 */
export interface AggregateAllResponse {
  portfolios: Array<{
    id: string
    name: string
    isDefault: boolean
    // Options metrics
    openPositions: number
    closedPositions: number
    unrealizedPnl: number
    realizedPnl: number
    totalPnl: number
    exposure: number
    capitalAtRisk: number
    delta: number
    gamma: number
    theta: number
    vega: number
    // Stock metrics
    stockOpenPositions: number
    stockClosedPositions: number
    stockUnrealizedPnl: number
    stockRealizedPnl: number
    stockTotalPnl: number
    dayPnl: number
    dayRoi: number | null
    stockDayPnl: number
    stockDayRoi: number | null
    stockExposure: number
    stockCapitalAtRisk: number
    // Combined metrics
    combinedOpenPositions: number
    combinedClosedPositions: number
    combinedUnrealizedPnl: number
    combinedRealizedPnl: number
    combinedTotalPnl: number
    combinedDayPnl: number
    combinedDayRoi: number | null
    combinedExposure: number
    combinedCapitalAtRisk: number
    cashBalance?: number
    cashAllocationPct?: number
    stockAllocationPct?: number
    optionAllocationPct?: number
    totalPortfolioValue?: number
    valuation?: PortfolioValuation
    // Undefined risk flag
    hasUndefinedRiskWithoutCollateral?: boolean
  }>
  valuation?: PortfolioValuation
  cashExposure?: Array<{ ticker: string; value: number }>

  // Options-only aggregate
  aggregate: {
    totalPositions: number
    openPositions: number
    closedPositions: number
    totalPnl: number
    unrealizedPnl: number
    realizedPnl: number
    portfolioDelta: number
    portfolioGamma: number
    portfolioTheta: number
    portfolioVega: number
    averageRoi: number
    winRate: number
  }
  // Options P&L history
  pnl: {
    realized: Array<{ date: string; value: number }>
    unrealized: Array<{ date: string; value: number }>
  }
  // Options exposure by ticker
  exposure: Array<{ ticker: string; value: number }>
  // Options Greeks
  greeks: {
    delta: number
    gamma: number
    theta: number
    vega: number
  }
  // Stock data
  hasStocks: boolean
  stockMetrics: {
    totalPositions: number
    openPositions: number
    closedPositions: number
    unrealizedPnl: number
    realizedPnl: number
    totalPnl: number
    capitalAtRisk: number
  }
  // Stock exposure by ticker
  stockExposure: Array<{ ticker: string; value: number }>
  // Stock P&L (current snapshot)
  stockPnl: {
    current: {
      unrealized: number
      realized: number
      total: number
    }
    snapshots?: Array<{
      date: string
      unrealized: number
      realized: number
      total: number
      label: string
    }>
  }
  dailyHoldingPnl?: Record<
    string,
    {
      stocks: number
      options: number
      total: number
      stocksAllTimePnl?: number
      optionsAllTimePnl?: number
      allTimePnl?: number
      stocksPct: number | null
      optionsPct: number | null
      totalPct: number | null
      stocksCurrentValue: number
      optionsCurrentValue: number
      currentValue: number
      stocksBaselineUnavailableCount?: number
      optionsBaselineUnavailableCount?: number
      stocksMovementAvailable?: boolean
      optionsMovementAvailable?: boolean
      stocksMovementDate?: string | null
      optionsMovementDate?: string | null
      stocksCurrentPriceUnavailableCount?: number
      optionsCurrentPriceUnavailableCount?: number
      stockDebug?: {
        currentPrice: number | null
        previousClose: number | null
        previousCloseDate: string | null
        /** Actual comparison price date; null means no single dated reference. */
        comparisonPriceDate?: string | null
        currentPriceDate: string | null
        currentPriceSource: string | null
        quantityAbs: number
        positionsCount: number
      }
      optionDebug?: {
        currentPremium: number | null
        previousClose: number | null
        previousCloseDate: string | null
        /** Actual comparison price date; null means no single dated reference. */
        comparisonPriceDate?: string | null
        currentPremiumDate: string | null
        currentPremiumSource: string | null
        contractsAbs: number
        positionsCount: number
        symbols?: string[]
        legs?: Array<{
          symbol: string
          contracts: number
          currentPremium: number
          previousClose: number | null
          previousCloseDate: string | null
          /** Actual comparison price date; null means no single dated reference. */
          comparisonPriceDate?: string | null
          referenceDate: string | null
          source: string | null
          todayPnl: number
        }>
      }
    }
  >
  // Combined aggregate for "All Assets" view
  combinedAggregate: {
    totalPositions: number
    openPositions: number
    closedPositions: number
    totalPnl: number
    unrealizedPnl: number
    realizedPnl: number
  }
  totalTrades: number
  totalStockPositions: number
}

/**
 * Get aggregated dashboard data for ALL portfolios in a single request.
 *
 * This is optimized for the "All Portfolios" dashboard view, fetching all data
 * in a single API call instead of 16+ separate calls.
 *
 * @param days - Number of days for historical P&L (default 90)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Aggregated dashboard data for all portfolios
 */
export async function getAggregateAll(
  days: number = 90,
  signal?: AbortSignal
): Promise<AggregateAllResponse> {
  return get<AggregateAllResponse>(
    `/api/dashboard/aggregate-all?days=${days}`,
    undefined,
    signal ? { signal } : undefined
  )
}

// ============================================================================
// CSV Import/Export API Methods
// ============================================================================

/**
 * Upload and parse a CSV file
 *
 * @param file - CSV file to upload
 * @param portfolioId - Optional target portfolio for duplicate preview checks
 * @returns Parsed positions and broker detection
 */
export async function uploadCSV(file: File, portfolioId?: string): Promise<CSVImportResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (portfolioId) {
    formData.append('portfolio_id', portfolioId)
  }

  return postFormData<CSVImportResponse>('/api/csv/import', formData)
}

/**
 * Confirm and import parsed positions
 *
 * @param positions - Parsed positions to import
 * @returns Import results with success/failure counts
 */
export async function confirmImport(
  positions: ConfirmImportRequest
): Promise<ConfirmImportResponse> {
  const needsSnapshot =
    !getAnalyticsFlag(FIRST_POSITION_ANALYTICS_KEY) ||
    !getAnalyticsFlag(FIRST_OPTION_POSITION_ANALYTICS_KEY)
  const snapshot = needsSnapshot ? await getPositionCountSnapshot() : null
  const result = await post<ConfirmImportResponse>('/api/csv/confirm-import', positions)
  const importedOptions = Number(result.imported_count || 0)
  const importedStocks = Number(result.stock_imported_count || 0)

  if (importedOptions + importedStocks > 0) {
    trackPositionActivationAfterCreate(snapshot, 'csv_import', importedOptions)
  }

  return result
}

/**
 * Export positions as CSV
 *
 * @param params - Export parameters (status filter)
 * @returns CSV file as Blob
 */
export async function exportCSV(params?: { status?: 'open' | 'closed' }): Promise<Blob> {
  const headers = await getAuthHeaders()
  const url = new URL(`${API_URL}/api/csv/export`)

  if (params?.status) {
    url.searchParams.append('status', params.status)
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    const errorData: APIError = await response.json()
    const message =
      typeof errorData.detail === 'string'
        ? errorData.detail
        : errorData.error || 'Failed to export CSV'
    throw new Error(message)
  }

  return response.blob()
}

// ============================================================================
// Auth API Methods
// ============================================================================

/**
 * Get current user profile
 *
 * @returns User profile with premium status
 */
export async function getProfile(init?: RequestInit): Promise<{
  id: string
  email: string
  created_at: string
  updated_at: string
  is_premium: boolean
  is_admin?: boolean
  access_status?: AccessStatus
  can_access_app?: boolean
  requires_upgrade?: boolean
  free_plan_end_date?: string | null
  legacy_free_plan?: boolean
  has_premium_access?: boolean
  has_paid_premium?: boolean
  can_use_broker_sync?: boolean
  subscription_provider?: BillingProvider | 'legacy' | null
  active_subscription_providers?: BillingProvider[]
  billing_relationships?: BillingRelationship[]
  multiple_active_billing_relationships?: boolean
  apple_subscription_active?: boolean
  google_play_subscription_active?: boolean
  stripe_subscription_active?: boolean
  billing_source_label?: string | null
  billing_environment?: 'production' | 'sandbox' | null
  management_url?: string | null
  full_name?: string
  avatar_url?: string
}> {
  return get('/api/auth/me', undefined, init, true)
}

/**
 * Delete user account and all associated data (GDPR-compliant)
 *
 * @returns Success response
 */
export async function deleteAccount(
  acknowledgeStoreSubscriptions = false
): Promise<{ success: boolean; message: string }> {
  const suffix = acknowledgeStoreSubscriptions ? '?acknowledge_store_subscriptions=true' : ''
  return del<{ success: boolean; message: string }>(`/api/auth/delete-account${suffix}`)
}

/**
 * Reset all portfolio trading data across all portfolios.
 * Preserves portfolio rows, account, subscription, profile, and affiliate data.
 *
 * @returns Success response with deleted row counts per table
 */
export async function resetPortfolioData(): Promise<{
  success: boolean
  portfolio_count: number
  deleted: Record<string, number>
}> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_URL}/api/account/reset-portfolio-data`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ confirmation: 'RESET' }),
  })
  return handleResponse(response)
}

export async function getEmailPreferences(): Promise<EmailPreferences> {
  return get<EmailPreferences>('/api/email-preferences')
}

export async function updateEmailPreferences(
  updates: EmailPreferencesUpdate
): Promise<EmailPreferences> {
  return put<EmailPreferences>('/api/email-preferences', updates)
}

export interface PositionEvent {
  id: string
  user_id: string
  event_type:
    | 'position_created'
    | 'position_updated'
    | 'position_closed_manual'
    | 'position_expired_worthless'
    | 'position_expired_itm'
    | 'position_assigned'
    | 'position_exercised'
    | 'underlying_created'
    | 'underlying_closed'
  event_date: string
  position_id: string | null
  underlying_position_id: string | null
  event_data: Record<string, unknown> | null
  created_at: string
}

export interface PositionEventList {
  events: PositionEvent[]
  total: number
}

/**
 * Get lifecycle position events for timestamp-precise wheel reconstruction.
 *
 * @param limit - Maximum number of lifecycle events to fetch
 * @returns Lifecycle event audit records
 */
export async function getPositionEvents(limit: number = 2000): Promise<PositionEventList> {
  return get<PositionEventList>('/api/position-events', { limit })
}

// ============================================================================
// Wheel Enrollment API Methods
// ============================================================================

export interface WheelEnrollment {
  id: string
  user_id: string
  portfolio_id: string
  ticker: string
  anchor_position_id: string
  source: 'manual' | 'csv_import'
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface WheelEnrollmentList {
  enrollments: WheelEnrollment[]
  total: number
}

/**
 * Get active wheel enrollments for the current user
 *
 * @param params - Optional filters (portfolio_id, all_portfolios)
 * @returns List of active wheel enrollments
 */
export async function getWheelEnrollments(params?: {
  portfolio_id?: string
  all_portfolios?: boolean
}): Promise<WheelEnrollmentList> {
  return get<WheelEnrollmentList>(
    '/api/wheel-enrollments',
    params as Record<string, string | number | boolean>
  )
}

/**
 * Create a new wheel enrollment (track a wheel cycle)
 *
 * @param data - Enrollment data (portfolio_id, ticker, anchor_position_id)
 * @returns Created enrollment
 */
export async function createWheelEnrollment(data: {
  portfolio_id: string
  ticker: string
  anchor_position_id: string
  source?: string
}): Promise<WheelEnrollment> {
  return post<WheelEnrollment>('/api/wheel-enrollments', data)
}

/**
 * Remove a wheel enrollment (soft-delete)
 *
 * @param id - Enrollment UUID
 * @returns Success response
 */
export async function deleteWheelEnrollment(
  id: string
): Promise<{ success: boolean; message: string }> {
  return del<{ success: boolean; message: string }>(`/api/wheel-enrollments/${id}`)
}

// ============================================================================
// Lifecycle API Methods
// ============================================================================

/**
 * Exercise a long CALL option
 *
 * @param positionId - UUID of the position
 * @param exercisePrice - Price at which to exercise (usually strike)
 * @param exerciseDate - Date of exercise
 * @returns Result with closed option position and new underlying position
 */
export async function exerciseLongCall(
  positionId: string,
  exercisePrice: number,
  exerciseDate: string
): Promise<{ option: Position; underlying: unknown }> {
  return post(`/api/lifecycle/exercise-long-call/${positionId}`, {
    exercise_price: exercisePrice,
    exercise_date: exerciseDate,
  })
}

/**
 * Exercise a long PUT option
 *
 * @param positionId - UUID of the position
 * @param exercisePrice - Price at which to exercise (usually strike)
 * @param exerciseDate - Date of exercise
 * @returns Result with closed option position and underlying position action
 */
export async function exerciseLongPut(
  positionId: string,
  exercisePrice: number,
  exerciseDate: string
): Promise<{ option: Position; underlying: unknown }> {
  return post(`/api/lifecycle/exercise-long-put/${positionId}`, {
    exercise_price: exercisePrice,
    exercise_date: exerciseDate,
  })
}

/**
 * Mark an option position as assigned (short) or exercised (long)
 *
 * @param positionId - UUID of the position
 * @param eventTimestamp - ISO timestamp of the event
 * @param notes - Optional notes
 * @returns Result with closed option position and underlying action
 */
export async function markAssignedOrExercised(
  positionId: string,
  eventTimestamp: string,
  quantity?: number,
  notes?: string | null
): Promise<{ success: boolean; position: Position; message?: string }> {
  return post(`/api/lifecycle/mark-assigned-exercised/${positionId}`, {
    event_timestamp: eventTimestamp,
    quantity: quantity ?? null,
    notes: notes ?? null,
  })
}

// ============================================================================
// Cash Ledger API Methods
// ============================================================================

/**
 * Get cash ledger entries with optional filters
 *
 * @param params - Query parameters for filtering (transaction_type, start_date, end_date, limit, portfolio_id)
 * @returns List of cash ledger entries
 */
export async function getCashLedgerEntries(params?: {
  transaction_type?: string
  category?: string
  start_date?: string
  end_date?: string
  limit?: number
  portfolio_id?: string
  broker_account_id?: string
}): Promise<CashLedgerEntry[]> {
  const response = await get<{ entries: CashLedgerEntry[]; count: number }>(
    '/api/cash-ledger',
    params as Record<string, string | number | boolean>
  )
  return response.entries
}

/**
 * Get cash ledger summary statistics
 *
 * @param params - Optional portfolio_id to filter by
 * @returns Summary with balance, totals, and transaction count
 */
export async function getCashLedgerSummary(params?: {
  portfolio_id?: string
  broker_account_id?: string
  start_date?: string
  end_date?: string
}): Promise<CashLedgerSummary> {
  return get<CashLedgerSummary>('/api/cash-ledger/summary', params as Record<string, string>)
}

/**
 * Create a manual cash ledger entry (deposit, withdrawal, or balance adjustment)
 *
 * @param entry - Cash ledger entry data
 * @returns Created cash ledger entry
 */
export async function createCashLedgerEntry(
  entry: CashLedgerEntryCreate
): Promise<CashLedgerEntry> {
  return post<CashLedgerEntry>('/api/cash-ledger', entry)
}

/**
 * Delete a single cash ledger entry
 *
 * @param entryId - ID of the entry to delete
 * @returns Result with success status
 */
export async function deleteCashLedgerEntry(
  entryId: string
): Promise<{ success: boolean; message: string }> {
  return del<{ success: boolean; message: string }>(`/api/cash-ledger/${entryId}`)
}

/**
 * Delete all cash ledger entries for the current user
 *
 * @returns Result with deleted count
 */
export async function deleteCashLedgerEntries(portfolioId?: string): Promise<{
  success: boolean
  message: string
  deleted_count: number
}> {
  const params = portfolioId ? `?portfolio_id=${portfolioId}` : ''
  return del<{ success: boolean; message: string; deleted_count: number }>(
    `/api/cash-ledger/all${params}`
  )
}

// ============================================================================
// Market Data API Methods (Premium Features)
// ============================================================================

export interface MarketDataStatus {
  is_real_time_enabled: boolean
  data_delay: string
  last_updated: string | null
  refresh_available: boolean
  message: string
}

export interface RefreshResponse {
  success: boolean
  positions_updated: number
  positions_considered?: number | null
  is_real_time: boolean
  message: string
}

export interface HistoricalPricesResponse {
  prices: Record<string, number | null>
  asOfUsed: string
  providerUsed: string
}

/**
 * Get market data status for current user
 *
 * @returns Market data access status
 */
export async function getMarketDataStatus(): Promise<MarketDataStatus> {
  return get<MarketDataStatus>('/api/market-data/status')
}

/**
 * Refresh market data for the selected portfolio scope (Premium only)
 * Fetches updated market data for premium users.
 *
 * @param portfolioId - Optional portfolio scope. If omitted, refreshes all portfolios.
 * @returns Refresh result with count of updated positions
 */
export async function refreshMarketData(portfolioId?: string): Promise<RefreshResponse> {
  const endpoint = portfolioId
    ? `/api/market-data/refresh?portfolio_id=${encodeURIComponent(portfolioId)}`
    : '/api/market-data/refresh'
  return post<RefreshResponse>(endpoint)
}

/**
 * Get historical prices for a batch of symbols at or before a date.
 *
 * @param symbols - Stock ticker symbols
 * @param asOf - Reference date (YYYY-MM-DD)
 * @returns Historical prices mapped by symbol
 */
export async function getHistoricalPrices(
  symbols: string[],
  asOf: string
): Promise<HistoricalPricesResponse> {
  return post<HistoricalPricesResponse>('/api/market-data/historical-prices', {
    symbols,
    as_of: asOf,
  })
}

/**
 * Search for symbols/companies via market data service
 *
 * @param query - Search query (e.g., "AAPL" or "Apple")
 * @param limit - Max results (default 10)
 * @returns List of normalized symbol results
 */
export async function searchSymbols(
  query: string,
  limit: number = 10
): Promise<
  Array<{
    symbol: string
    name: string
    exchange: string
    type: string
    currency: string
  }>
> {
  return get('/api/market-data/search', { q: query, limit })
}

export interface PrivateSearchResponse {
  results: Array<{
    id: string
    kind:
      | 'portfolio'
      | 'option_position'
      | 'stock_position'
      | 'stock_trade'
      | 'scenario'
      | 'saved_screen'
    title: string
    context: string
    destination: string
    status?: string
    symbol?: string
    score?: number
  }>
  has_more: boolean
  next_cursor: string | null
  errors: Array<{ source: string; code: string }>
  scope: string
}

/** Query stays in a no-store POST body, never a browser URL or a client log. */
export async function searchPrivateRecords(
  query: string,
  signal: AbortSignal,
  cursor?: string
): Promise<PrivateSearchResponse> {
  return post(
    '/api/search/private',
    { query, limit: 6, ...(cursor ? { cursor } : {}) },
    { signal, cache: 'no-store' }
  )
}

export async function searchUniversalSymbols(
  query: string,
  signal: AbortSignal
): ReturnType<typeof searchSymbols> {
  return post('/api/search/symbols', { query, limit: 4 }, { signal, cache: 'no-store' })
}

/**
 * Get available expiration dates for a symbol
 *
 * @param ticker - Stock ticker symbol
 * @returns List of expiration dates (YYYY-MM-DD)
 */
export async function getOptionExpirations(ticker: string): Promise<string[]> {
  const response = await get<{ ticker: string; expirations: string[] }>(
    `/api/market-data/options/expirations/${ticker}`
  )
  return response.expirations
}

// ============================================================================
// Underlying (Stock) Positions API Methods
// ============================================================================

export interface UnderlyingPosition {
  currency?: string | null
  portfolio_id?: string | null
  id: string
  user_id: string
  ticker: string
  quantity: number
  side: 'long' | 'short'
  entry_price: number
  entry_date: string
  entry_source: 'manual' | 'assignment' | 'exercise' | 'imported'
  status: 'open' | 'closed'
  exit_price?: number | null
  exit_date?: string | null
  exit_reason?: 'manual' | 'assignment' | 'exercise' | 'imported' | null
  notes?: string | null
  tags?: string[] | null
  related_option_id?: string | null
  created_at: string
  updated_at: string
}

export interface UnderlyingPositionWithPnL extends UnderlyingPosition {
  pnl: number
  pnl_type: 'unrealized' | 'realized'
  roi: number
  capital_at_risk: number
  current_price: number | null
  days_held: number
  realized_pnl?: number | null
  price_observation?: ManualStockPriceObservation | null
}

export type ManualStockPriceObservation = PriceObservation

export interface UnderlyingPositionUpdate {
  currency?: string | null
  quantity?: number
  entry_price?: number
  entry_date?: string
  notes?: string
  tags?: string[]
  current_price?: number
  price_observation?: ManualStockPriceObservation
  exit_price?: number
  exit_date?: string
}

export interface UnderlyingPositionCreate {
  currency?: string | null
  ticker: string
  quantity: number
  side: 'long' | 'short'
  entry_price: number
  entry_date: string
  notes?: string
  tags?: string[]
  portfolio_id?: string
}

/**
 * Create a new underlying (stock) position
 *
 * @param data - Position data
 * @returns Created underlying position
 */
export async function createUnderlyingPosition(
  data: UnderlyingPositionCreate
): Promise<UnderlyingPosition> {
  // Add entry_source as 'manual' for user-created positions
  const snapshot = getAnalyticsFlag(FIRST_POSITION_ANALYTICS_KEY)
    ? null
    : await getPositionCountSnapshot()
  const created = await post<UnderlyingPosition>('/api/underlying-positions', {
    ...data,
    entry_source: 'manual',
  })
  trackPositionActivationAfterCreate(snapshot, 'manual_stock')
  return created
}

/**
 * Get all underlying (stock) positions
 *
 * @param status - Optional filter by 'open' or 'closed'
 * @param portfolioId - Optional portfolio ID to filter by
 * @returns List of underlying positions with P&L
 */
export async function getUnderlyingPositions(
  status?: 'open' | 'closed',
  portfolioId?: string,
  allPortfolios: boolean = false
): Promise<{
  positions: UnderlyingPositionWithPnL[]
  total_count: number
  open_count: number
  closed_count: number
}> {
  const params: Record<string, string> = {}
  if (status) params.status = status
  if (allPortfolios) {
    params.all_portfolios = 'true'
  } else if (portfolioId) {
    params.portfolio_id = portfolioId
  }
  return get('/api/underlying-positions', params)
}

/**
 * Get a single underlying position by ID
 *
 * @param positionId - UUID of the position
 * @returns Underlying position with P&L
 */
export async function getUnderlyingPosition(
  positionId: string,
  includeMarketData = true
): Promise<UnderlyingPositionWithPnL> {
  return get(`/api/underlying-positions/${positionId}`, { include_market_data: includeMarketData })
}

/**
 * Update an underlying (stock) position
 *
 * @param positionId - UUID of the position
 * @param update - Fields to update
 * @returns Updated position with P&L
 */
export async function updateUnderlyingPosition(
  positionId: string,
  update: UnderlyingPositionUpdate
): Promise<UnderlyingPositionWithPnL> {
  return put(`/api/underlying-positions/${positionId}`, update)
}

/**
 * Close an underlying position (sell stock)
 *
 * @param positionId - UUID of the position
 * @param exitPrice - Price at which stock was sold
 * @returns Closed position
 */
export async function closeUnderlyingPosition(
  positionId: string,
  exitPrice: number
): Promise<UnderlyingPosition> {
  // exit_price is a query parameter, not body
  return post(`/api/underlying-positions/${positionId}/close?exit_price=${exitPrice}`)
}

import type {
  StockTradeCreate,
  StockTradeResponse,
  StockTrade,
  StockTradeWithPosition,
} from '../types/position'

/**
 * Record a buy or sell trade against an existing stock position
 *
 * @param positionId - UUID of the position
 * @param data - Trade data (action, shares, price, trade_date, notes)
 * @returns Trade response with updated position state
 */
export async function recordStockTrade(
  positionId: string,
  data: StockTradeCreate
): Promise<StockTradeResponse> {
  return post<StockTradeResponse>(`/api/underlying-positions/${positionId}/trade`, data)
}

/**
 * Get portfolio-level stock trade history.
 *
 * Includes buys and sells so broker-synced trade history matches the activity
 * ledger, while the UI only shows realized P&L for sell events.
 */
export async function getStockTradeHistory(
  portfolioId?: string,
  allPortfolios: boolean = false
): Promise<StockTradeWithPosition[]> {
  const params: Record<string, string> = {}
  if (allPortfolios) {
    params.all_portfolios = 'true'
  } else if (portfolioId) {
    params.portfolio_id = portfolioId
  }
  return get<StockTradeWithPosition[]>('/api/underlying-positions/stock-trades', params)
}

/**
 * Get trade history for a stock position
 *
 * @param positionId - UUID of the position
 * @returns List of stock trades ordered by date (most recent first)
 */
export async function getStockTrades(positionId: string): Promise<StockTrade[]> {
  return get<StockTrade[]>(`/api/underlying-positions/${positionId}/trades`)
}

export async function getStockTradeById(tradeId: string): Promise<StockTradeWithPosition> {
  return get<StockTradeWithPosition>(`/api/underlying-positions/stock-trades/${tradeId}`)
}

/**
 * Delete an underlying position
 *
 * @param positionId - UUID of the position
 */
export async function deleteUnderlyingPosition(positionId: string): Promise<void> {
  return del(`/api/underlying-positions/${positionId}`)
}

/**
 * Delete ALL underlying (stock) positions for current user
 *
 * @returns Count of deleted positions
 */
export async function deleteAllUnderlyingPositions(): Promise<{
  deleted_count: number
  message: string
}> {
  return del<{ deleted_count: number; message: string }>('/api/underlying-positions/all')
}

/**
 * Delete only OPEN underlying (stock) positions for current user
 *
 * @param portfolioId - Optional portfolio ID to filter deletion. If not provided, deletes from ALL portfolios.
 * @returns Count of deleted positions
 */
export async function deleteAllOpenUnderlyingPositions(portfolioId?: string): Promise<{
  deleted_count: number
  message: string
}> {
  const params = portfolioId ? `?portfolio_id=${portfolioId}` : ''
  return del<{ deleted_count: number; message: string }>(
    `/api/underlying-positions/all/open${params}`
  )
}

/**
 * Delete only CLOSED underlying (stock) positions (trade history) for current user
 *
 * @param portfolioId - Optional portfolio ID to filter deletion. If not provided, deletes from ALL portfolios.
 * @returns Count of deleted positions
 */
export async function deleteAllClosedUnderlyingPositions(portfolioId?: string): Promise<{
  deleted_count: number
  message: string
}> {
  const params = portfolioId ? `?portfolio_id=${portfolioId}` : ''
  return del<{ deleted_count: number; message: string }>(
    `/api/underlying-positions/all/closed${params}`
  )
}

// ============================================================================
// Alert Preferences (Premium Feature)
// ============================================================================

export interface AlertPreferences {
  email_enabled: boolean
  alert_profit_target: boolean
  alert_stop_loss: boolean
  alert_expiration_warning: boolean
  alert_assignment_risk: boolean
  alert_daily_summary: boolean
  profit_threshold_percent: number
  loss_threshold_percent: number
  days_before_expiration: number
  assignment_risk_dte: number
  assignment_risk_delta: number
  daily_summary_hour: number
  auto_exercise_long_itm: boolean
}

export interface AlertPreferencesUpdate {
  email_enabled?: boolean
  alert_profit_target?: boolean
  alert_stop_loss?: boolean
  alert_expiration_warning?: boolean
  alert_assignment_risk?: boolean
  alert_daily_summary?: boolean
  profit_threshold_percent?: number
  loss_threshold_percent?: number
  days_before_expiration?: number
  assignment_risk_dte?: number
  assignment_risk_delta?: number
  daily_summary_hour?: number
  auto_exercise_long_itm?: boolean
}

export interface NotificationPreferences extends AlertPreferences {
  push_enabled: boolean
  profit_target_email_enabled: boolean
  profit_target_push_enabled: boolean
  stop_loss_email_enabled: boolean
  stop_loss_push_enabled: boolean
  expiry_warning_email_enabled: boolean
  expiry_warning_push_enabled: boolean
  auto_exercise_email_enabled: boolean
  auto_exercise_push_enabled: boolean
  push_redact_sensitive_details: boolean
  app_alerts_entitled: boolean
  entitlement_reason: string
}

export interface NotificationPreferencesUpdate extends AlertPreferencesUpdate {
  push_enabled?: boolean
  profit_target_email_enabled?: boolean
  profit_target_push_enabled?: boolean
  stop_loss_email_enabled?: boolean
  stop_loss_push_enabled?: boolean
  expiry_warning_email_enabled?: boolean
  expiry_warning_push_enabled?: boolean
  auto_exercise_email_enabled?: boolean
  auto_exercise_push_enabled?: boolean
  push_redact_sensitive_details?: boolean
}

export interface PushTokenRegistration {
  platform: 'android' | 'ios'
  token: string
  device_id?: string | null
  app_version?: string | null
  os_version?: string | null
  environment?: string
  permission_status?: string | null
}

export interface NotificationEvent {
  id: string
  alert_type: string
  severity: string
  title: string
  body: string
  position_id?: string | null
  strategy_id?: string | null
  trade_id?: string | null
  data: Record<string, unknown>
  read_at?: string | null
  expires_at?: string | null
  created_at: string
}

export interface NotificationsList {
  notifications: NotificationEvent[]
  unread_count: number
  limit: number
  offset: number
}

/**
 * Get user's alert preferences (Premium only)
 *
 * @returns User's alert preferences
 */
export async function getAlertPreferences(): Promise<AlertPreferences> {
  return get<AlertPreferences>('/api/alerts/preferences')
}

/**
 * Update user's alert preferences (Premium only)
 *
 * @param preferences - Partial update of alert preferences
 * @returns Updated alert preferences
 */
export async function updateAlertPreferences(
  preferences: AlertPreferencesUpdate
): Promise<AlertPreferences> {
  return put<AlertPreferences>('/api/alerts/preferences', preferences)
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return get<NotificationPreferences>('/api/notification-preferences')
}

export async function updateNotificationPreferences(
  preferences: NotificationPreferencesUpdate
): Promise<NotificationPreferences> {
  return patch<NotificationPreferences>('/api/notification-preferences', preferences)
}

export async function registerPushToken(token: PushTokenRegistration): Promise<{
  id: string
  platform: string
  token: string
  enabled: boolean
  last_seen_at?: string | null
}> {
  return post('/api/mobile/push-tokens', token)
}

export async function revokeCurrentPushToken(params: {
  token?: string | null
  device_id?: string | null
}): Promise<{ success: boolean; message: string }> {
  const search = new URLSearchParams()
  if (params.token) search.set('token', params.token)
  if (params.device_id) search.set('device_id', params.device_id)
  const suffix = search.toString() ? `?${search.toString()}` : ''
  return del(`/api/mobile/push-tokens/current${suffix}`)
}

export async function getNotifications(params?: {
  limit?: number
  offset?: number
  unread_only?: boolean
}): Promise<NotificationsList> {
  return get('/api/notifications', params as Record<string, string | number | boolean>)
}

export async function markNotificationRead(eventId: string): Promise<NotificationEvent> {
  return post(`/api/notifications/${eventId}/read`)
}

export async function sendTestPushNotification(): Promise<{
  event_id: string
  attempted: number
  sent: number
  failed: number
  skipped: number
  deliveries?: Array<{
    push_token_id: string | null
    status: string
    error_code: string | null
    error_message: string | null
  }>
}> {
  return post('/api/mobile/push-test')
}

// ============================================================================
// Stripe Subscription API Methods
// ============================================================================

export interface PricingInfo {
  monthly_price: number
  yearly_price: number
  yearly_discount_percent: number
  currency: string
  features: string[]
  free_plan_removal_enabled?: boolean
  free_plan_end_date?: string | null
}

export type BillingProvider = 'stripe' | 'apple' | 'google_play'

export interface BillingRelationship {
  provider: BillingProvider
  provider_label: string
  status: string
  product_id?: string | null
  base_plan_id?: string | null
  period_type?: 'trial' | 'introductory' | 'normal' | null
  environment?: 'production' | 'sandbox' | null
  period_end?: string | null
  management_url?: string | null
  will_renew: boolean
  is_trial: boolean
  is_paid: boolean
  has_access?: boolean
}

export interface SubscriptionStatus {
  is_premium: boolean
  status:
    | 'free'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'trialing'
    | 'unpaid'
    | 'incomplete'
    | 'incomplete_expired'
    | 'grace_period'
    | 'billing_issue'
    | 'pending'
    | 'account_hold'
    | 'paused'
    | 'expired'
    | 'refunded'
    | 'revoked'
    | 'inactive'
    | 'sandbox_blocked'
  subscription_id: string | null
  period_end: string | null
  is_founding_member?: boolean
  founding_member_since?: string | null
  founding_member_discount_percent?: number
  access_status?: AccessStatus
  can_access_app?: boolean
  requires_upgrade?: boolean
  upgrade_error_code?: string | null
  access_message?: string | null
  free_plan_end_date?: string | null
  legacy_free_plan?: boolean
  trial_available?: boolean
  trial_consumed?: boolean
  has_premium_access?: boolean
  has_paid_premium?: boolean
  can_use_broker_sync?: boolean
  subscription_provider?: BillingProvider | 'legacy' | null
  active_subscription_providers?: string[]
  billing_source_label?: string | null
  billing_environment?: 'production' | 'sandbox' | null
  management_url?: string | null
  apple_subscription_active?: boolean
  google_play_subscription_active?: boolean
  stripe_subscription_active?: boolean
  multiple_active_billing_relationships?: boolean
  billing_relationships?: BillingRelationship[]
}

export type AccessStatus =
  | 'admin'
  | 'premium_active'
  | 'premium_past_due'
  | 'trial_active'
  | 'trial_expired'
  | 'legacy_free_active'
  | 'legacy_free_expired'
  | 'no_plan'

/**
 * Trial usage status for trial users
 */
export interface TrialUsage {
  screener: { used: number; limit: number }
  discovery: { runs_by_scan: Record<string, number>; limit_per_scan: number }
  watchlist: { limit: number }
  resets_at: string
}

export interface TrialUsageStatus {
  status:
    | 'free'
    | 'trialing'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'unpaid'
    | 'incomplete'
    | 'incomplete_expired'
    | 'grace_period'
    | 'billing_issue'
    | 'pending'
    | 'account_hold'
    | 'paused'
    | 'expired'
    | 'refunded'
    | 'revoked'
    | 'inactive'
    | 'sandbox_blocked'
  is_premium: boolean
  is_trial: boolean
  trial_ends_at?: string | null
  trial_usage?: TrialUsage | null
  period_end?: string | null
  is_founding_member?: boolean
  access_status: AccessStatus
  can_access_app: boolean
  requires_upgrade: boolean
  upgrade_error_code?: string | null
  access_message?: string | null
  free_plan_end_date?: string | null
  legacy_free_plan?: boolean
  has_premium_access?: boolean
  has_paid_premium?: boolean
  can_use_broker_sync?: boolean
  subscription_provider?: BillingProvider | 'legacy' | null
  active_subscription_providers?: string[]
  billing_source_label?: string | null
  management_url?: string | null
  apple_subscription_active?: boolean
  google_play_subscription_active?: boolean
  stripe_subscription_active?: boolean
  multiple_active_billing_relationships?: boolean
  billing_relationships?: BillingRelationship[]
}

export interface RevenueCatSyncResponse {
  synchronized: boolean
  has_premium_access: boolean
  has_paid_premium: boolean
  can_use_broker_sync: boolean
  subscription_provider: BillingProvider | 'legacy' | null
  status: string
  is_trial: boolean
  product_id: string | null
  environment: 'production' | 'sandbox'
  period_end: string | null
  management_url: string | null
  billing_relationships: BillingRelationship[]
}

export async function syncRevenueCatSubscription(): Promise<RevenueCatSyncResponse> {
  return post<RevenueCatSyncResponse>('/api/revenuecat/sync', {})
}

export interface AcquisitionEligibility {
  allowed: boolean
  denial_reason: string | null
  target_provider: BillingProvider
  billing_relationships: BillingRelationship[]
  current_access_expiration: string | null
  app_update_required: boolean
  google_play_acquisition_disabled_globally: boolean
  minimum_android_version_code: number
}

export interface AcquisitionReservation {
  allowed: boolean
  reservation_token: string | null
  denial_reason: string | null
  expires_at: string | null
}

export async function getAcquisitionEligibility(
  targetProvider: BillingProvider,
  platform?: 'web' | 'ios' | 'android',
  androidVersionCode?: number
): Promise<AcquisitionEligibility> {
  const params = new URLSearchParams({ target_provider: targetProvider })
  if (platform) params.set('platform', platform)
  if (androidVersionCode) params.set('android_version_code', String(androidVersionCode))
  return get<AcquisitionEligibility>(`/api/subscription/acquisition-eligibility?${params}`)
}

export async function reserveAcquisition(
  targetProvider: BillingProvider,
  platform?: 'web' | 'ios' | 'android',
  androidVersionCode?: number
): Promise<AcquisitionReservation> {
  return post<AcquisitionReservation>('/api/subscription/acquisition-reservations', {
    target_provider: targetProvider,
    platform,
    android_version_code: androidVersionCode,
  })
}

export async function releaseAcquisition(reservationToken: string): Promise<{ released: boolean }> {
  return post<{ released: boolean }>('/api/subscription/acquisition-reservations/release', {
    reservation_token: reservationToken,
  })
}

export interface CheckoutSessionResponse {
  checkout_url: string
}

export interface VerifiedTrialStartedResponse {
  trialStarted: boolean
  subscriptionId: string
  marketingEligible: boolean
  conversionId: string | null
}

export interface PortalSessionResponse {
  portal_url: string
}

export interface ConvertTrialResponse {
  subscription_id: string
  status: string
}

export interface CancelSubscriptionResponse {
  status: string
  cancel_at_period_end: boolean
  period_end: number | null
}

export type SignupDeviceType = 'desktop' | 'mobile' | 'tablet' | 'unknown'
export type SignupSurface = 'web' | 'android_app' | 'ios_app' | 'unknown'
export type FunnelEventName =
  | 'landing_viewed'
  | 'signup_viewed'
  | 'signup_started'
  | 'signup_completed'
  | 'trial_activation_page_viewed'
  | 'trial_plan_selected'
  | 'checkout_started'
  | 'checkout_failed'
  | 'trial_activated'
  | 'subscription_first_payment_succeeded'
  | 'subscription_first_payment_failed'
  | 'subscription_cancelled_during_trial'

export interface SignupAttributionPayload {
  signup_started_at?: string
  device_type?: SignupDeviceType
  signup_surface?: SignupSurface
  operating_system?: string
  browser?: string
  app_version?: string
  app_build?: string
  initial_landing_path?: string
  signup_path?: string
  referrer?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  anonymous_id?: string
}

export interface FunnelEventPayload extends SignupAttributionPayload {
  event_name: FunnelEventName
  selected_plan?: 'monthly' | 'yearly' | 'unknown'
  landing_path?: string
  billing_error_category?:
    | 'unavailable'
    | 'already_purchased'
    | 'network'
    | 'interrupted'
    | 'not_allowed'
    | 'configuration'
    | 'failed'
  billing_error_code?: string
}

export async function saveSignupAttribution(payload: SignupAttributionPayload) {
  return post<{ saved: boolean; created: boolean }>('/api/funnel/signup-attribution', payload)
}

export async function trackFunnelEventRequest(payload: FunnelEventPayload) {
  return post<{ recorded: boolean; reason?: string }>('/api/funnel/events', payload)
}

export interface MeasurementOutcome {
  id: string
  event_name:
    | 'signup_completed'
    | 'checkout_started'
    | 'trial_activated'
    | 'subscription_first_payment_succeeded'
  occurred_at: string
}

export async function getMeasurementOutcomes(): Promise<{ events: MeasurementOutcome[] }> {
  return get('/api/funnel/measurement-outcomes', undefined, { signal: AbortSignal.timeout(700) })
}

export async function submitTrialActivationFeedback(payload: {
  token: string
  reason:
    | 'card_not_ready'
    | 'expected_free_plan'
    | 'price_too_high'
    | 'wanted_broker_sync'
    | 'mobile_activation_problem'
    | 'wanted_product_exploration'
    | 'other'
  details?: string
}) {
  return post<{ saved: boolean; message: string }>('/api/funnel/trial-feedback', payload)
}

export interface AdminFunnelReport {
  date_range: { start: string; end: string }
  metrics: {
    accounts_created: number
    signup_completions_recorded?: number
    trial_activation_page_views: number
    checkout_starts: number
    trials_activated: number
    trial_activation_rate: number | null
    checkout_completion_rate: number | null
    first_successful_subscription_payments: number
    trial_to_paid_conversion_rate: number | null
  }
  group_by: string
  groups: Array<{
    value: string
    accounts_created: number
    checkout_starts: number
    trials_activated: number
    trial_activation_rate: number | null
    checkout_completion_rate: number | null
    first_payments: number
    trial_to_paid_conversion_rate: number | null
  }>
  users: Array<{
    user_id: string
    email: string
    signup_date: string
    device_type: SignupDeviceType
    signup_surface: SignupSurface
    signup_segment: string
    operating_system: string | null
    app_version: string | null
    initial_landing_path: string | null
    referrer: string | null
    utm_source: string | null
    utm_medium: string | null
    utm_campaign: string | null
    utm_content: string | null
    utm_term: string | null
    trial_page_viewed: boolean
    checkout_started: boolean
    trial_activated: boolean
    signup_completed_recorded?: boolean
    first_payment_recorded?: boolean
    selected_billing_plan: 'monthly' | 'yearly' | null
    current_subscription_state: string
    current_subscription_provider?: string
    currently_trialing?: boolean
    trial_measurement_source?: 'live' | 'test' | 'historical_or_unverified' | 'not_recorded'
    trial_activation_feedback: string | null
    trial_activation_feedback_details: string | null
  }>
}

export async function getAdminFunnelReport(params: {
  start_date?: string
  end_date?: string
  group_by?: string
  signup_segment?: string
  utm_source?: string
  utm_campaign?: string
  initial_landing_path?: string
  selected_plan?: 'monthly' | 'yearly'
}): Promise<AdminFunnelReport> {
  return get<AdminFunnelReport>('/api/admin/funnel', params)
}

const NATIVE_MOBILE_BUILD = import.meta.env.MODE === 'mobile'
const NATIVE_PAYMENT_ACTIONS_DISABLED =
  'Subscription payment actions are disabled in native mobile builds.'

async function nativeSubscriptionPaymentActionDisabled(): Promise<never> {
  throw new Error(NATIVE_PAYMENT_ACTIONS_DISABLED)
}

/**
 * Get pricing information for premium plans
 *
 * @returns Pricing details for display
 */
export async function getPricing(): Promise<PricingInfo> {
  return get<PricingInfo>('/api/stripe/pricing')
}

/**
 * Get current subscription status
 *
 * @returns User's subscription status
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  return get<SubscriptionStatus>('/api/stripe/subscription')
}

/** Verify a Checkout Session directly with Stripe before recording a trial conversion. */
export async function verifyTrialStarted(sessionId: string): Promise<VerifiedTrialStartedResponse> {
  const verified = await post<VerifiedTrialStartedResponse>('/api/stripe/verify-trial-started', {
    session_id: sessionId,
  })
  if (verified.trialStarted && verified.marketingEligible && verified.conversionId) {
    const { sendVerifiedStripeTrialConversion } = await import('./consent/googleTag')
    sendVerifiedStripeTrialConversion(verified.conversionId)
  }
  return verified
}

/** Close a canceled, server-issued Checkout generation before releasing its lease. */
export async function cancelCheckout(reservationToken: string): Promise<{ canceled: boolean }> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      post<{ canceled: boolean }>(
        '/api/stripe/cancel-checkout',
        { reservation_token: reservationToken },
        { signal: controller.signal }
      ),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort()
          reject(new Error('Checkout cancellation timed out. Please retry cancellation.'))
        }, 10_000)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Get trial usage status (uses new /api/subscription/status endpoint)
 *
 * Includes trial usage info for trial users.
 * Concurrent default reads share one request for the current session; completed
 * responses are not cached. Custom request options retain independent transport.
 *
 * @returns User's subscription and trial usage status
 */
export async function getTrialUsageStatus(init?: RequestInit): Promise<TrialUsageStatus> {
  return get<TrialUsageStatus>('/api/subscription/status', undefined, init, true)
}

/**
 * Create a Stripe Checkout session for subscribing to Premium
 *
 * @param plan - Monthly or yearly subscription plan
 * @param applyFoundingMemberDiscount - Legacy flag. The backend rejects true because the offer is closed.
 * @returns Checkout session URL to redirect to
 */
export async function createCheckoutSession(
  plan: 'monthly' | 'yearly' = 'monthly',
  applyFoundingMemberDiscount?: boolean,
  startTrial: boolean = true
): Promise<CheckoutSessionResponse> {
  // Import consent check dynamically to avoid circular dependencies
  const { hasConsent } = await import('./consent/storage')

  // Only read DataFast cookies if analytics consent is granted
  let datafastVisitorId: string | undefined
  let datafastSessionId: string | undefined

  if (hasConsent('analytics')) {
    const getCookie = (name: string): string | undefined => {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop()?.split(';').shift()
      return undefined
    }

    datafastVisitorId = getCookie('datafast_visitor_id')
    datafastSessionId = getCookie('datafast_session_id')
  }

  const response = await post<CheckoutSessionResponse>('/api/stripe/create-checkout-session', {
    plan,
    start_trial: startTrial,
    apply_founding_member_discount: applyFoundingMemberDiscount,
    datafast_visitor_id: datafastVisitorId,
    datafast_session_id: datafastSessionId,
  })
  // Emit the server's canonical checkout outcome before navigation to Stripe.
  let measurementTimer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      import('./funnelMeasurement').then(({ syncWebMeasurementOutcomes }) =>
        syncWebMeasurementOutcomes(() => true, true)
      ),
      new Promise<void>((resolve) => {
        measurementTimer = setTimeout(resolve, 750)
      }),
    ])
  } catch {
    // An analytics import/request failure never turns a successful checkout into an error.
  } finally {
    if (measurementTimer) clearTimeout(measurementTimer)
  }
  return response
}

/**
 * Create a Stripe Customer Portal session for managing subscription
 *
 * @returns Portal session URL to redirect to
 */
export async function createPortalSession(): Promise<PortalSessionResponse> {
  return post<PortalSessionResponse>('/api/stripe/create-portal-session', {})
}

export interface AffiliateProgramSettings {
  program_enabled: boolean
  default_commission_bps: number
  attribution_window_days?: number
  hold_period_days: number
  payout_minimum_cents: number
  leaderboard_public: boolean
  auto_approve_affiliates?: boolean
  feature_request_quota_per_quarter?: number
  next_payout_date?: string
}

export interface AffiliateLeaderboardEntry {
  affiliate_profile_id: string
  referral_code: string
  display_name: string
  conversion_count: number
  influenced_mrr_cents: number
  rank: number
  approved_commission_cents?: number
  paid_commission_cents?: number
}

export interface AffiliateProfile {
  id: string
  user_id: string
  status: 'pending' | 'approved' | 'blocked'
  referral_code: string
  display_name: string | null
  stripe_connected_account_id: string | null
  stripe_onboarding_status: 'not_started' | 'pending' | 'completed' | 'restricted' | 'disabled'
  stripe_details_submitted: boolean
  stripe_payouts_enabled: boolean
  stripe_charges_enabled: boolean
  stripe_requirements_currently_due: string[]
  stripe_requirements_eventually_due: string[]
  stripe_requirements_past_due: string[]
  stripe_restrictions: Record<string, unknown>
  stripe_disabled_reason: string | null
  latest_payout_id: string | null
  latest_payout_status: string | null
  latest_payout_failure_message: string | null
  latest_payout_at: string | null
  payout_minimum_cents: number | null
  commission_bps: number | null
  code_last_changed_at: string | null
  code_change_count: number
  joined_at: string
  approved_at: string | null
  blocked_at: string | null
}

export interface AffiliatePayoutRunItem {
  id: string
  payout_run_id: string
  affiliate_profile_id: string
  connected_account_id: string | null
  commission_ids: string[]
  commission_count: number
  commission_total_cents: number
  transfer_amount_cents: number
  currency: string
  minimum_threshold_cents: number
  status: 'pending' | 'transferred' | 'paid' | 'retryable' | 'failed' | 'skipped'
  transfer_id: string | null
  payout_id: string | null
  payout_status: string | null
  failure_reason: string | null
  created_at: string
  paid_at: string | null
}

export interface AffiliateFeatureRequest {
  id: string
  affiliate_profile_id: string
  user_id: string
  quarter_key: string
  title: string
  description: string
  status: 'submitted' | 'reviewing' | 'considered' | 'planned' | 'declined'
  admin_notes: string | null
  created_at: string
  reviewed_at: string | null
}

export interface AffiliateReferralActivity {
  id: string
  affiliate_profile_id: string
  referral_code_snapshot: string
  referred_user_id: string | null
  referred_user_name: string | null
  referred_user_email: string | null
  landing_path: string
  utm_source: string | null
  review_status: 'unreviewed' | 'reviewing' | 'cleared' | 'blocked'
  is_eligible: boolean
  ineligibility_reason: string | null
  created_at: string
  attributed_at: string | null
}

export interface AffiliateDashboardResponse {
  settings: AffiliateProgramSettings
  affiliate_profile: AffiliateProfile | null
  stats: {
    referrals: number
    paid_conversions: number
    pending_commission_cents: number
    approved_commission_cents: number
    paid_commission_cents: number
    total_commission_cents: number
    influenced_mrr_cents: number
  }
  leaderboard_position: number | null
  payout_history: AffiliatePayoutRunItem[]
  feature_requests: AffiliateFeatureRequest[]
  recent_referrals: AffiliateReferralActivity[]
  public_leaderboard: {
    monthly: AffiliateLeaderboardEntry[]
    all_time: AffiliateLeaderboardEntry[]
  }
}

export interface AffiliateAdminAffiliateRow extends AffiliateProfile {
  referrals_count: number
  monthly_revenue_generated_cents: number
  total_earned_cents: number
  total_revenue_generated_cents: number
}

export interface AffiliatePublicProgramResponse {
  settings: AffiliateProgramSettings
  leaderboard: {
    monthly: AffiliateLeaderboardEntry[]
    all_time: AffiliateLeaderboardEntry[]
  }
}

export interface AffiliateAdminOverview {
  settings: AffiliateProgramSettings
  affiliates: AffiliateAdminAffiliateRow[]
  suspicious_referrals: Array<Record<string, unknown>>
  feature_requests: AffiliateFeatureRequest[]
  payout_runs: Array<Record<string, unknown>>
}

export async function getAffiliatePublicProgram(): Promise<AffiliatePublicProgramResponse> {
  const response = await fetch(`${API_URL}/api/affiliate/public`)
  return handleResponse<AffiliatePublicProgramResponse>(response)
}

export async function getAffiliateLeaderboard(
  scope: 'monthly' | 'all_time'
): Promise<{ rows: AffiliateLeaderboardEntry[] }> {
  const response = await fetch(`${API_URL}/api/affiliate/leaderboard?scope=${scope}`)
  return handleResponse<{ rows: AffiliateLeaderboardEntry[] }>(response)
}

export async function captureAffiliateReferral(payload: {
  referral_code: string
  browser_id: string
  landing_path: string
  referral_source: 'query' | 'path' | 'manual' | 'admin'
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_content?: string | null
  utm_term?: string | null
}): Promise<{ success: boolean; referral: Record<string, unknown> }> {
  return post('/api/affiliate/capture', payload)
}

export async function claimAffiliateReferral(browser_id: string): Promise<Record<string, unknown>> {
  return post('/api/affiliate/claim', { browser_id })
}

export async function joinAffiliateProgram(): Promise<AffiliateProfile> {
  return post('/api/affiliate/join', {})
}

export async function getAffiliateDashboard(): Promise<AffiliateDashboardResponse> {
  return get('/api/affiliate/dashboard')
}

export async function updateAffiliateReferralCode(
  referral_code: string
): Promise<AffiliateProfile> {
  return post('/api/affiliate/referral-code', { referral_code })
}

export async function createAffiliateConnectAccount(): Promise<AffiliateProfile> {
  return post('/api/affiliate/connect/account', {})
}

export async function createAffiliateOnboardingLink(): Promise<{
  url: string
  expires_at?: number
}> {
  return post('/api/affiliate/connect/onboarding-link', {})
}

export async function createAffiliateDashboardLoginLink(): Promise<{
  url: string
}> {
  return post('/api/affiliate/connect/dashboard-link', {})
}

export async function submitAffiliateFeatureRequest(payload: {
  title: string
  description: string
}): Promise<AffiliateFeatureRequest> {
  return post('/api/affiliate/feature-requests', payload)
}

export async function getAffiliateAdminOverview(): Promise<AffiliateAdminOverview> {
  return get('/api/affiliate/admin/overview')
}

export async function updateAffiliateAdminSettings(
  updates: Partial<AffiliateProgramSettings> & { auto_approve_affiliates?: boolean }
): Promise<AffiliateProgramSettings> {
  return put('/api/affiliate/admin/settings', updates)
}

export async function runAffiliatePayoutBatch(): Promise<Record<string, unknown>> {
  return post('/api/affiliate/admin/payout-runs', {})
}

export async function sendAffiliateLaunchCampaign(
  limit: number = 500
): Promise<Record<string, unknown>> {
  return post('/api/affiliate/admin/campaigns/launch', { limit })
}

export async function sendAffiliateEngagedInviteCampaign(
  limit: number = 250
): Promise<Record<string, unknown>> {
  return post('/api/affiliate/admin/campaigns/engaged-invite', { limit })
}

export async function sendAffiliateQuarterlyUpdateCampaign(
  highlight: string,
  limit: number = 500
): Promise<Record<string, unknown>> {
  return post('/api/affiliate/admin/campaigns/quarterly-update', { limit, highlight })
}

export async function updateAffiliateStatus(
  affiliate_profile_id: string,
  statusValue: 'pending' | 'approved' | 'blocked'
): Promise<AffiliateProfile> {
  return post(`/api/affiliate/admin/affiliates/${affiliate_profile_id}/status`, {
    status: statusValue,
  })
}

export async function adminUpdateAffiliateReferralCode(
  affiliate_profile_id: string,
  referral_code: string
): Promise<AffiliateProfile> {
  return post(`/api/affiliate/admin/affiliates/${affiliate_profile_id}/referral-code`, {
    referral_code,
  })
}

export async function reviewAffiliateReferral(
  referral_id: string,
  review_status: 'reviewing' | 'cleared' | 'blocked',
  review_notes?: string
): Promise<Record<string, unknown>> {
  return post(`/api/affiliate/admin/referrals/${referral_id}/review`, {
    review_status,
    review_notes,
  })
}

export async function updateAffiliateFeatureRequestStatus(
  feature_request_id: string,
  statusValue: 'submitted' | 'reviewing' | 'considered' | 'planned' | 'declined',
  admin_notes?: string
): Promise<AffiliateFeatureRequest> {
  return post(`/api/affiliate/admin/feature-requests/${feature_request_id}/status`, {
    status: statusValue,
    admin_notes,
  })
}

/**
 * Cancel the user's premium subscription
 *
 * @param cancelImmediately - If true, cancel immediately; otherwise cancel at period end
 * @returns Cancellation result
 */
export async function cancelSubscription(
  cancelImmediately: boolean = false
): Promise<CancelSubscriptionResponse> {
  return post<CancelSubscriptionResponse>('/api/stripe/cancel', {
    cancel_immediately: cancelImmediately,
  })
}

/**
 * Convert a trialing subscription to paid immediately.
 * Ends the trial and starts billing now without creating a new subscription.
 *
 * @returns The updated subscription status
 */
export async function convertTrialToPaid(): Promise<ConvertTrialResponse> {
  return post<ConvertTrialResponse>('/api/stripe/convert-trial', {})
}

// ============================================================================
// Strategy API Methods
// ============================================================================

/**
 * Get all strategies with optional filters
 *
 * @param params - Query parameters for filtering
 * @returns List of strategies with counts
 */
export async function getStrategies(params?: {
  portfolio_id?: string
  status?: StrategyStatus
  strategy_type?: StrategyType
  limit?: number
  offset?: number
}): Promise<StrategyList> {
  return get<StrategyList>('/api/strategies', params as Record<string, string | number | boolean>)
}

/**
 * Get strategies with full leg data and metrics
 *
 * @param params - Query parameters for filtering
 * @returns List of strategies with legs and metrics
 */
export async function getStrategiesWithLegs(params?: {
  portfolio_id?: string
  status?: StrategyStatus
  limit?: number
  offset?: number
}): Promise<StrategyWithLegsList> {
  return get<StrategyWithLegsList>(
    '/api/strategies/with-legs',
    params as Record<string, string | number | boolean>
  )
}

/**
 * Get a specific strategy by ID with legs and metrics
 *
 * @param id - Strategy UUID
 * @returns Strategy with legs and metrics
 */
export async function getStrategy(id: string): Promise<StrategyWithLegs> {
  return get<StrategyWithLegs>(`/api/strategies/${id}`)
}

/**
 * Create a new strategy
 *
 * @param strategy - Strategy data to create
 * @returns Created strategy
 */
export async function createStrategy(strategy: StrategyCreate): Promise<Strategy> {
  const created = await post<Strategy>('/api/strategies', strategy)
  trackDataFastActivationEvent('strategy_created', { source: 'strategy_builder' })
  return created
}

/**
 * Update an existing strategy
 *
 * @param id - Strategy UUID
 * @param updates - Partial strategy data to update
 * @returns Updated strategy
 */
export async function updateStrategy(id: string, updates: StrategyUpdate): Promise<Strategy> {
  return put<Strategy>(`/api/strategies/${id}`, updates)
}

/**
 * Delete a strategy
 *
 * @param id - Strategy UUID
 * @returns Success response
 */
export async function deleteStrategy(id: string): Promise<{ success: boolean; message: string }> {
  return del<{ success: boolean; message: string }>(`/api/strategies/${id}`)
}

/**
 * Add a leg to a strategy
 *
 * @param strategyId - Strategy UUID
 * @param leg - Leg data
 * @returns Created leg
 */
export async function addStrategyLeg(
  strategyId: string,
  leg: StrategyLegCreate
): Promise<StrategyLeg> {
  return post<StrategyLeg>(`/api/strategies/${strategyId}/legs`, leg)
}

/**
 * Remove a leg from a strategy
 *
 * @param strategyId - Strategy UUID
 * @param legId - Leg UUID
 * @returns Success response
 */
export async function removeStrategyLeg(
  strategyId: string,
  legId: string
): Promise<{ success: boolean; message: string }> {
  return del<{ success: boolean; message: string }>(`/api/strategies/${strategyId}/legs/${legId}`)
}

/**
 * Close all legs of a strategy
 *
 * @param strategyId - Strategy UUID
 * @param exitDate - Date to close legs
 * @returns Updated strategy with all legs closed
 */
export async function closeStrategy(
  strategyId: string,
  exitDate: string
): Promise<StrategyWithLegs> {
  return post<StrategyWithLegs>(`/api/strategies/${strategyId}/close?exit_date=${exitDate}`)
}

/**
 * Validate a strategy configuration
 *
 * @param strategyType - Type of strategy
 * @param optionPositionIds - Option position IDs
 * @param underlyingPositionIds - Underlying position IDs
 * @returns Validation result with computed metrics
 */
export async function validateStrategy(
  strategyType: StrategyType,
  optionPositionIds?: string[],
  underlyingPositionIds?: string[]
): Promise<StrategyValidationResult> {
  const params = new URLSearchParams()
  params.append('strategy_type', strategyType)
  if (optionPositionIds) {
    optionPositionIds.forEach((id) => params.append('option_position_ids', id))
  }
  if (underlyingPositionIds) {
    underlyingPositionIds.forEach((id) => params.append('underlying_position_ids', id))
  }
  return post<StrategyValidationResult>(`/api/strategies/validate?${params.toString()}`)
}

/**
 * Get all strategy type definitions/metadata
 *
 * @returns List of strategy metadata
 */
export async function getStrategyTypes(): Promise<StrategyMetadata[]> {
  return get<StrategyMetadata[]>('/api/strategies/types')
}

/**
 * Get metadata for a specific strategy type
 *
 * @param strategyType - Strategy type
 * @returns Strategy metadata
 */
export async function getStrategyTypeMetadata(
  strategyType: StrategyType
): Promise<StrategyMetadata> {
  return get<StrategyMetadata>(`/api/strategies/types/${strategyType}`)
}

// ============================================================================
// Options Screener API Methods
// ============================================================================

import type {
  ExpiryFilter,
  OptionContractCompact,
  OptionsChainResponse,
  ScreenerResponse,
} from '../types/screener'
import type {
  ScanId,
  ScanListResponse,
  ScanResultsResponse,
  ScanPreviewResponse,
} from '../types/scans'
import type {
  DiscoveryConfiguration,
  SavedDiscoveryScreen,
  SavedDiscoveryScreenList,
  SavedDiscoveryScreenRunResult,
} from '../types/discovery'

/**
 * Get options chain for screening
 *
 * @param symbol - Underlying ticker symbol (e.g., SPY)
 * @param expiryFilter - Coarse expiry filter (this_week, this_month, next_3_months, all)
 * @returns Options chain with contracts and cache metadata
 */
export async function getOptionsChain(
  symbol: string,
  expiryFilter: ExpiryFilter = 'this_month'
): Promise<OptionsChainResponse> {
  return get<OptionsChainResponse>(`/api/market/options/${symbol.toUpperCase()}/chain`, {
    expiry_filter: expiryFilter,
  })
}

/** Resolve one exact option contract independently from the candidate chain. */
export async function getOptionContract(
  symbol: string,
  contractSymbol: string
): Promise<OptionContractCompact> {
  return get<OptionContractCompact>(
    `/api/market/options/${symbol.toUpperCase()}/contract/${encodeURIComponent(contractSymbol)}`
  )
}

/**
 * Quote response for stock price and IV
 */
export interface QuoteResponse {
  symbol: string
  price: number | null
  price_source: 'market_data' | null
  price_timestamp: string | null
  iv: number | null
  iv_source: 'market_data' | null
  iv_timestamp: string | null
  error: string | null
}

/**
 * Get stock quote and ATM IV for POP calculation
 *
 * @param symbol - Underlying ticker symbol
 * @returns Quote with price, IV, and user-safe source metadata
 */
export async function getQuote(symbol: string): Promise<QuoteResponse> {
  return get<QuoteResponse>(`/api/market/options/${symbol.toUpperCase()}/quote`)
}

// ============================================================================
// Scenario Lab API Methods (Premium)
// ============================================================================

export async function analyzeScenario(request: ScenarioAnalyzeRequest): Promise<ScenarioAnalysis> {
  return post<ScenarioAnalysis>('/api/scenario-lab/analyze', request)
}

export async function listScenarios(portfolioId?: string): Promise<ScenarioList> {
  return get<ScenarioList>(
    '/api/scenario-lab',
    portfolioId ? { portfolio_id: portfolioId } : undefined
  )
}

export async function getScenario(id: string): Promise<Scenario> {
  return get<Scenario>(`/api/scenario-lab/${id}`)
}

export async function createScenario(request: ScenarioCreate): Promise<Scenario> {
  return post<Scenario>('/api/scenario-lab', request)
}

export async function updateScenario(id: string, request: ScenarioUpdate): Promise<Scenario> {
  return put<Scenario>(`/api/scenario-lab/${id}`, request)
}

export async function duplicateScenario(
  id: string,
  request?: { use_current_portfolio: boolean }
): Promise<Scenario> {
  return post<Scenario>(`/api/scenario-lab/${id}/duplicate`, request)
}

export async function deleteScenario(id: string): Promise<void> {
  await del(`/api/scenario-lab/${id}`)
}

export async function prepareScenarioOrder(request: OrderIntentRequest): Promise<OrderIntent> {
  return post<OrderIntent>('/api/scenario-lab/order-intent/prepare', request)
}

// ============================================================================
// Options Discovery Scans API Methods (Premium)
// ============================================================================

/**
 * List all available discovery scans
 *
 * Public endpoint - shows scan names and descriptions to all users.
 * @returns List of available scans with metadata
 */
export async function listScans(): Promise<ScanListResponse> {
  return get<ScanListResponse>('/api/options/scans')
}

/**
 * Execute a discovery scan (Premium only)
 *
 * @param scanId - The scan identifier
 * @returns Scan results with matching stocks and options
 * @throws 403 if user is not Premium
 */
export async function executeScan(scanId: ScanId): Promise<ScanResultsResponse> {
  return get<ScanResultsResponse>(`/api/options/scans/${scanId}`)
}

/**
 * Get scan preview info (Public)
 *
 * @param scanId - The scan identifier
 * @returns Scan metadata and upgrade message
 */
export async function getScanPreview(scanId: ScanId): Promise<ScanPreviewResponse> {
  return get<ScanPreviewResponse>(`/api/options/scans/${scanId}/preview`)
}

export async function listSavedDiscoveryScreens(): Promise<SavedDiscoveryScreenList> {
  return get<SavedDiscoveryScreenList>('/api/options/saved-screens')
}

export async function createSavedDiscoveryScreen(
  name: string,
  configuration: DiscoveryConfiguration
): Promise<SavedDiscoveryScreen> {
  return post<SavedDiscoveryScreen>('/api/options/saved-screens', { name, configuration })
}

export async function updateSavedDiscoveryScreen(
  screenId: string,
  changes: {
    name?: string
    configuration?: DiscoveryConfiguration
    alertsEnabled?: boolean
    alertCadenceMinutes?: number
  }
): Promise<SavedDiscoveryScreen> {
  return patch<SavedDiscoveryScreen>(`/api/options/saved-screens/${screenId}`, {
    ...(changes.name !== undefined ? { name: changes.name } : {}),
    ...(changes.configuration !== undefined ? { configuration: changes.configuration } : {}),
    ...(changes.alertsEnabled !== undefined ? { alerts_enabled: changes.alertsEnabled } : {}),
    ...(changes.alertCadenceMinutes !== undefined
      ? { alert_cadence_minutes: changes.alertCadenceMinutes }
      : {}),
  })
}

export async function duplicateSavedDiscoveryScreen(
  screenId: string,
  name?: string
): Promise<SavedDiscoveryScreen> {
  return post<SavedDiscoveryScreen>(`/api/options/saved-screens/${screenId}/duplicate`, { name })
}

export async function deleteSavedDiscoveryScreen(screenId: string): Promise<void> {
  return del<void>(`/api/options/saved-screens/${screenId}`)
}

export async function recordSavedDiscoveryScreenRun(
  screenId: string,
  matchKeys: string[]
): Promise<SavedDiscoveryScreenRunResult> {
  return post<SavedDiscoveryScreenRunResult>(`/api/options/saved-screens/${screenId}/runs`, {
    match_keys: matchKeys,
  })
}

// ============================================================================
// Feedback API Methods
// ============================================================================

import type {
  BugReportCreate,
  BugReportResponse,
  FeatureRequestCreate,
  FeatureRequest,
  FeatureRequestListResponse,
  VoteToggleResponse,
  ScreenshotUploadResponse,
  FeatureListParams,
  AdminCheckResponse,
  FeatureStatus,
} from '../types/feedback'

/**
 * Submit a bug report
 *
 * @param data - Bug report data with message, optional title, screenshot, and context
 * @returns Created bug report response
 */
export async function submitBugReport(data: BugReportCreate): Promise<BugReportResponse> {
  return post<BugReportResponse>('/api/feedback/bug', data)
}

/**
 * Submit a feature request
 *
 * @param data - Feature request data with title, description, area, and context
 * @returns Created feature request
 */
export async function submitFeatureRequest(data: FeatureRequestCreate): Promise<FeatureRequest> {
  return post<FeatureRequest>('/api/feedback/feature', data)
}

export async function submitInactiveUserFeedback(data: {
  token: string
  comment?: string
}): Promise<{
  received: boolean
  message: string
  additional_feedback: boolean
  duplicate: boolean
}> {
  return post('/api/feedback/inactive', data)
}

/**
 * Get list of feature requests with pagination
 *
 * @param params - Query parameters (q, area, sort, limit, offset)
 * @returns Paginated list of feature requests
 */
export async function getFeatureRequests(
  params?: FeatureListParams
): Promise<FeatureRequestListResponse> {
  return get<FeatureRequestListResponse>(
    '/api/feedback/feature',
    params as Record<string, string | number | boolean>
  )
}

/**
 * Get a single feature request by ID
 *
 * @param featureId - Feature request UUID
 * @returns Feature request with user_has_voted
 */
export async function getFeatureRequest(featureId: string): Promise<FeatureRequest> {
  return get<FeatureRequest>(`/api/feedback/feature/${featureId}`)
}

/**
 * Toggle vote on a feature request
 *
 * @param featureId - Feature request UUID
 * @returns Updated vote count and user's vote status
 */
export async function toggleFeatureVote(featureId: string): Promise<VoteToggleResponse> {
  return post<VoteToggleResponse>(`/api/feedback/feature/${featureId}/vote`)
}

/**
 * Upload a screenshot for feedback
 *
 * @param file - PNG, JPEG, or WebP image file (max 2MB)
 * @returns Signed URL for the uploaded screenshot
 */
export async function uploadFeedbackScreenshot(file: Blob): Promise<ScreenshotUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return postFormData<ScreenshotUploadResponse>('/api/feedback/screenshot', formData)
}

/**
 * Check if current user is an admin
 *
 * @returns Admin check response with is_admin boolean
 */
export async function checkFeedbackAdminStatus(): Promise<AdminCheckResponse> {
  return get<AdminCheckResponse>('/api/feedback/admin-check')
}

/**
 * Update feature request status (admin only)
 *
 * @param featureId - Feature request ID
 * @param status - New status value
 * @returns Updated feature request
 */
export async function updateFeatureStatus(
  featureId: string,
  status: FeatureStatus
): Promise<FeatureRequest> {
  return patch<FeatureRequest>(`/api/feedback/feature/${featureId}/status`, { status })
}

// ============================================================================
// Admin Scan Snapshots API Methods (Admin Only)
// ============================================================================

export interface AdminDatesResponse {
  dates: string[]
  latest: string | null
}

export interface AdminHealthMetadata {
  total_count: number
  provider_split: Record<string, number>
  fallback_count: number
  limited_mode_count: number
}

export interface AdminSnapshotRow {
  id: string
  scan_id: string
  symbol: string
  as_of_date: string
  capture_type: string
  provider_used: string | null
  fallback_used: boolean
  limited_metrics_mode: boolean
  best_contract_symbol: string | null
  best_contract_score: number | null
  best_contract_pop: number | null
  best_contract_ev: number | null
  best_contract_roi_annual: number | null
  best_contract_premium: number | null
  best_contract_strike: number | null
  best_contract_dte: number | null
  contracts: Array<Record<string, unknown>> | null
}

export interface AdminSnapshotsListResponse {
  items: AdminSnapshotRow[]
  next_cursor: string | null
  health: AdminHealthMetadata
}

export interface AdminDistributionBucket {
  min_val: number
  max_val: number
  count: number
}

export interface AdminStatsResponse {
  total_snapshots: number
  days_with_data: number
  missing_days: string[]
  provider_counts: Record<string, number>
  fallback_rate: number
  limited_mode_rate: number
  score_distribution: AdminDistributionBucket[]
  pop_distribution: AdminDistributionBucket[]
  ev_distribution: AdminDistributionBucket[]
  roi_distribution: AdminDistributionBucket[]
  score_pop_correlation: number | null
  score_ev_correlation: number | null
  score_roi_correlation: number | null
}

export interface AdminAnomalyFlag {
  snapshot_id: string
  scan_id: string
  symbol: string
  as_of_date: string
  contract_symbol: string | null
  anomaly_type: 'high_roi' | 'invalid_pop' | 'zero_bid' | 'wide_spread' | 'missing_greeks'
  value: number | string | null
  threshold: number | string | null
}

export interface AdminAnomaliesResponse {
  items: AdminAnomalyFlag[]
  total_count: number
}

export interface AdminTopPickRow {
  rank: number
  underlying: string
  contract_symbol: string
  strike: number
  expiry: string
  option_type: string
  dte: number
  score: number
  score_v2: number | null
  pop: number
  ev: number
  roi_annual: number
  premium: number
  pnl_7d: number | null
  pnl_7d_pct: number | null
}

export interface AdminScanTopPicks {
  scan_id: string
  provider_used: string | null
  limited_metrics_mode: boolean
  picks: AdminTopPickRow[]
}

export interface AdminAllTopPicksResponse {
  as_of_date: string
  capture_type: string
  scans: AdminScanTopPicks[]
  total_picks: number
  v2_available: boolean
}

export interface AdminOutcomeAnalyticsRow {
  scan_id: string
  sample_size: number
  win_rate: number
  median_pnl: number
  avg_return: number
  baseline_sample_size: number
  baseline_win_rate: number
  baseline_median_pnl: number
  baseline_avg_return: number
  edge_win_rate: number | null
  edge_median_pnl: number | null
  edge_avg_return: number | null
}

export interface AdminOutcomeAnalyticsResponse {
  rows: AdminOutcomeAnalyticsRow[]
  outcome_window: string
  from_date: string | null
  to_date: string | null
}

export type BacktestHorizon = '3d' | '7d' | '14d' | '30d' | 'until_expiration'
export type BacktestRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

export interface BacktestRunSummary {
  pick_count: number
  metric_count: number
  scan_keys: string[]
  horizons: BacktestHorizon[]
  rank_sources: string[]
}

export interface BacktestRun {
  id: string
  name: string
  status: BacktestRunStatus
  from_date: string
  to_date: string
  scan_keys: string[]
  horizons: BacktestHorizon[]
  score_versions: string[]
  top_k: number
  random_seed?: number | null
  data_source?: string | null
  dataset_version?: string | null
  config_json?: Record<string, unknown>
  started_at: string | null
  completed_at: string | null
  created_at?: string
  updated_at?: string
  error_details?: string | null
  summary?: BacktestRunSummary
}

export interface BacktestRunMetric {
  id: string
  run_id: string
  scan_key: string | null
  rank_source: string
  score_version: string
  model_version: string | null
  horizon: BacktestHorizon
  top_k: number
  sample_size: number
  coverage: number | null
  win_rate: number | null
  median_pnl: number | null
  avg_return: number | null
  precision_at_k: number | null
  baseline_win_rate: number | null
  baseline_median_pnl: number | null
  baseline_avg_return: number | null
  edge_win_rate: number | null
  edge_median_pnl: number | null
  edge_avg_return: number | null
  metadata: Record<string, unknown>
  computed_at: string | null
  created_at?: string
}

export interface BacktestRunPick {
  id: string
  run_id: string
  as_of_date: string
  scan_key: string
  rank_source: string
  score_version: string
  model_version: string | null
  rank: number
  top_k: number
  contract_symbol: string
  underlying_symbol: string | null
  expiry: string | null
  strike: number | null
  option_right: 'call' | 'put' | null
  entry_mid: number | null
  score: number | null
  horizon: BacktestHorizon
  horizon_days: number | null
  target_exit_date: string
  exit_date: string | null
  exit_reason: string | null
  exit_mid: number | null
  pnl: number | null
  pnl_pct: number | null
  coverage_status: string | null
  metadata: Record<string, unknown>
  computed_at: string | null
  created_at?: string
}

export type BacktestMetricsGrouped = Record<
  string,
  Record<string, Record<string, BacktestRunMetric[]>>
>

export interface BacktestRunsListParams {
  scan_key?: string
  horizon?: BacktestHorizon
  rank_source?: string
  status?: BacktestRunStatus
  limit?: number
}

export interface BacktestRunDetailParams {
  include_picks?: boolean
  scan_key?: string
  horizon?: BacktestHorizon
  rank_source?: string
}

export interface BacktestRunsListResponse {
  items: BacktestRun[]
  total_count: number
  limit: number
}

export interface BacktestRunDetailResponse {
  run: BacktestRun
  metrics: BacktestRunMetric[]
  metrics_grouped: BacktestMetricsGrouped
  include_picks: boolean
  summary: BacktestRunSummary
  picks?: BacktestRunPick[]
}

/**
 * Check if current user is an admin (via profiles.is_admin)
 *
 * @returns Admin check response with is_admin boolean
 */
export async function checkAdminStatus(): Promise<{ is_admin: boolean }> {
  try {
    // Try to call any admin endpoint - if we get 403, not admin
    await get<AdminDatesResponse>('/api/admin/scan-snapshots/dates')
    return { is_admin: true }
  } catch (error: unknown) {
    if ((error as { status?: number }).status === 403) {
      return { is_admin: false }
    }
    // For other errors, assume not admin for safety
    return { is_admin: false }
  }
}

/**
 * Get available snapshot dates (admin only)
 *
 * @param captureType - Capture type filter
 * @returns List of dates and latest date
 */
export async function getAdminSnapshotDates(
  captureType: string = 'market_close'
): Promise<AdminDatesResponse> {
  return get<AdminDatesResponse>('/api/admin/scan-snapshots/dates', {
    capture_type: captureType,
  })
}

/**
 * Get paginated snapshot list (admin only)
 *
 * @param params - Query parameters
 * @returns Snapshot list with health metadata
 */
export async function getAdminSnapshots(params: {
  as_of_date: string
  capture_type?: string
  scan_id?: string
  provider_used?: string
  limited_metrics_mode?: boolean
  symbol_search?: string
  limit?: number
  cursor?: string
}): Promise<AdminSnapshotsListResponse> {
  const queryParams: Record<string, string | number | boolean> = {
    as_of_date: params.as_of_date,
    capture_type: params.capture_type || 'market_close',
  }
  if (params.scan_id) queryParams.scan_id = params.scan_id
  if (params.provider_used) queryParams.provider_used = params.provider_used
  if (params.limited_metrics_mode !== undefined)
    queryParams.limited_metrics_mode = params.limited_metrics_mode
  if (params.symbol_search) queryParams.symbol_search = params.symbol_search
  if (params.limit) queryParams.limit = params.limit
  if (params.cursor) queryParams.cursor = params.cursor

  return get<AdminSnapshotsListResponse>('/api/admin/scan-snapshots', queryParams)
}

/**
 * Get aggregated stats for a scan (admin only)
 *
 * @param params - Query parameters
 * @returns Aggregated statistics
 */
export async function getAdminSnapshotStats(params: {
  scan_id: string
  capture_type?: string
  from_date: string
  to_date: string
  rank_by?: 'v1' | 'v2'
}): Promise<AdminStatsResponse> {
  return get<AdminStatsResponse>('/api/admin/scan-snapshots/stats', {
    scan_id: params.scan_id,
    capture_type: params.capture_type || 'market_close',
    from_date: params.from_date,
    to_date: params.to_date,
  })
}

/**
 * Get anomalous contracts (admin only)
 *
 * @param params - Query parameters
 * @returns List of anomaly flags
 */
export async function getAdminSnapshotAnomalies(params: {
  scan_id: string
  from_date: string
  to_date: string
  capture_type?: string
  rank_by?: 'v1' | 'v2'
}): Promise<AdminAnomaliesResponse> {
  return get<AdminAnomaliesResponse>('/api/admin/scan-snapshots/anomalies', {
    scan_id: params.scan_id,
    from_date: params.from_date,
    to_date: params.to_date,
    capture_type: params.capture_type || 'market_close',
  })
}

/**
 * Get all top picks for all scans on a specific date (admin only)
 *
 * @param params - Query parameters
 * @returns All scans' top 5 picks
 */
export async function getAdminAllTopPicks(params: {
  as_of_date: string
  capture_type?: string
  rank_by?: 'v1' | 'v2'
}): Promise<AdminAllTopPicksResponse> {
  return get<AdminAllTopPicksResponse>('/api/admin/scan-snapshots/all-top-picks', {
    as_of_date: params.as_of_date,
    capture_type: params.capture_type || 'manual',
    rank_by: params.rank_by || 'v1',
  })
}

/**
 * Get scan outcomes analytics (admin only)
 */
export async function getAdminOutcomeAnalytics(params: {
  capture_type?: string
  from_date: string
  to_date: string
  outcome_window?: '7d' | '14d'
  pick_scope?: 'scan_top5' | 'scan_top5_v2'
  rank_by?: 'v1' | 'v2'
  score_version?: 'v1' | 'v2'
}): Promise<AdminOutcomeAnalyticsResponse> {
  const query: Record<string, string | number | boolean> = {
    capture_type: params.capture_type || 'market_close',
    from_date: params.from_date,
    to_date: params.to_date,
    outcome_window: params.outcome_window || '7d',
    pick_scope: params.pick_scope || 'scan_top5',
  }

  if (params.score_version) {
    query.score_version = params.score_version
  }

  return get<AdminOutcomeAnalyticsResponse>('/api/admin/scan-snapshots/analytics', query)
}

/**
 * Trigger scan outcome recomputation (admin only)
 */
export async function getAdminOutcomeRecompute(params: {
  allow_late?: boolean
}): Promise<{ status: string; allow_late: boolean }> {
  return post<{ status: string; allow_late: boolean }>('/api/admin/scan-snapshots/recompute', {
    allow_late: params.allow_late ?? false,
  })
}

/**
 * Trigger scan outcome recomputation for a date range (admin only)
 */
export async function getAdminOutcomeRecomputeRange(params: {
  from_date?: string
  to_date?: string
  days?: number
  capture_type?: string
  allow_late?: boolean
}): Promise<{
  status: string
  from_date?: string
  to_date?: string
  days?: number
  capture_type?: string
  allow_late: boolean
}> {
  return post<{
    status: string
    from_date?: string
    to_date?: string
    days?: number
    capture_type?: string
    allow_late: boolean
  }>('/api/admin/scan-snapshots/recompute-range', {
    from_date: params.from_date,
    to_date: params.to_date,
    days: params.days,
    capture_type: params.capture_type,
    allow_late: params.allow_late ?? true,
  })
}

/**
 * Trigger snapshot + outcome jobs (admin only)
 */
export async function runAdminScanJobs(params: {
  capture_type?: string
  allow_late?: boolean
}): Promise<{ status: string; capture_type: string; allow_late: boolean }> {
  return post<{ status: string; capture_type: string; allow_late: boolean }>(
    '/api/admin/scan-snapshots/run-both',
    {
      capture_type: params.capture_type || 'manual',
      allow_late: params.allow_late ?? false,
    }
  )
}

/**
 * Get recent historical backtest runs (admin only)
 */
export async function getAdminBacktestRuns(
  params: BacktestRunsListParams = {}
): Promise<BacktestRunsListResponse> {
  const query: Record<string, string | number | boolean> = {}
  if (params.scan_key) query.scan_key = params.scan_key
  if (params.horizon) query.horizon = params.horizon
  if (params.rank_source) query.rank_source = params.rank_source
  if (params.status) query.status = params.status
  if (params.limit) query.limit = params.limit

  return get<BacktestRunsListResponse>('/api/admin/backtest/runs', query)
}

/**
 * Get one historical backtest run with grouped metrics and optional picks (admin only)
 */
export async function getAdminBacktestRun(
  runId: string,
  params: BacktestRunDetailParams = {}
): Promise<BacktestRunDetailResponse> {
  const query: Record<string, string | number | boolean> = {}
  if (params.include_picks !== undefined) query.include_picks = params.include_picks
  if (params.scan_key) query.scan_key = params.scan_key
  if (params.horizon) query.horizon = params.horizon
  if (params.rank_source) query.rank_source = params.rank_source

  return get<BacktestRunDetailResponse>(`/api/admin/backtest/runs/${runId}`, query)
}

/**
 * Trigger asset fundamentals job (admin only)
 */
export async function runAssetFundamentalsJob(): Promise<{ status: string; message: string }> {
  return post<{ status: string; message: string }>('/api/admin/fundamentals/run')
}

export type EmailCampaignSegment = 'free' | 'trial' | 'premium' | 'all'
export type EmailCampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'queued'
  | 'sending'
  | 'completed'
  | 'cancelled'

export interface AdminEmailCampaignFilters {
  only_verified: boolean
  exclude_unsubscribed: boolean
  exclude_already_sent: boolean
}

export interface AdminEmailCampaignPayload {
  internal_name: string
  subject: string
  body_text: string
  cta_label: string
  cta_url: string
  segments: EmailCampaignSegment[]
  filters: AdminEmailCampaignFilters
  hard_cap?: number | null
}

export interface AdminEmailCampaign extends AdminEmailCampaignPayload {
  id: string
  status: EmailCampaignStatus
  created_by: string | null
  recipient_count: number
  sent_count: number
  failed_count: number
  skipped_count: number
  pending_count: number
  queued_at: string | null
  started_at: string | null
  completed_at: string | null
  cancelled_at: string | null
  created_at: string
  updated_at: string
}

export interface AdminEmailCampaignEstimate {
  estimated_count: number
  uncapped_count: number
  hard_cap: number | null
  excluded_counts: Record<string, number>
  filters: AdminEmailCampaignFilters
  segments: EmailCampaignSegment[]
}

export interface AdminEmailCampaignPreview {
  subject: string
  html: string
  text: string
}

export interface AdminEmailCampaignQueuePayload {
  batch_size: number
}

export async function getAdminEmailCampaigns(): Promise<{ campaigns: AdminEmailCampaign[] }> {
  return get<{ campaigns: AdminEmailCampaign[] }>('/api/admin/email-campaigns')
}

export async function createAdminEmailCampaign(
  payload: AdminEmailCampaignPayload
): Promise<{ campaign: AdminEmailCampaign }> {
  return post<{ campaign: AdminEmailCampaign }>('/api/admin/email-campaigns', payload)
}

export async function updateAdminEmailCampaign(
  campaignId: string,
  payload: Partial<AdminEmailCampaignPayload>
): Promise<{ campaign: AdminEmailCampaign }> {
  return put<{ campaign: AdminEmailCampaign }>(`/api/admin/email-campaigns/${campaignId}`, payload)
}

export async function estimateAdminEmailCampaign(payload: {
  segments: EmailCampaignSegment[]
  filters: AdminEmailCampaignFilters
  campaign_id?: string
  hard_cap?: number | null
}): Promise<AdminEmailCampaignEstimate> {
  return post<AdminEmailCampaignEstimate>('/api/admin/email-campaigns/estimate', payload)
}

export async function previewAdminEmailCampaign(payload: {
  subject: string
  body_text: string
  cta_label: string
  cta_url: string
}): Promise<AdminEmailCampaignPreview> {
  return post<AdminEmailCampaignPreview>('/api/admin/email-campaigns/preview', payload)
}

export async function sendAdminEmailCampaignTest(
  campaignId: string
): Promise<{ sent: boolean; email: string; message_id: string | null }> {
  return post<{ sent: boolean; email: string; message_id: string | null }>(
    `/api/admin/email-campaigns/${campaignId}/send-test`,
    {}
  )
}

export async function queueAdminEmailCampaign(
  campaignId: string,
  payload?: AdminEmailCampaignQueuePayload
): Promise<{ campaign: AdminEmailCampaign }> {
  return post<{ campaign: AdminEmailCampaign }>(
    `/api/admin/email-campaigns/${campaignId}/queue`,
    payload ?? {}
  )
}

export async function cancelAdminEmailCampaign(
  campaignId: string
): Promise<{ campaign: AdminEmailCampaign }> {
  return post<{ campaign: AdminEmailCampaign }>(
    `/api/admin/email-campaigns/${campaignId}/cancel`,
    {}
  )
}

// ============================================================================
// Admin SnapTrade API Methods
// ============================================================================

export async function getAdminSnapTradeStatus(): Promise<AdminSnapTradeStatus> {
  return get<AdminSnapTradeStatus>('/api/admin/snaptrade/status')
}

export async function getAdminBrokerSyncSupportUsers(params?: {
  search?: string
  status?: string
  limit?: number
}): Promise<{ users: AdminBrokerSyncSupportUser[] }> {
  return get('/api/admin/snaptrade/support-users', params)
}

export async function syncAdminBrokerSupportUser(
  userId: string
): Promise<AdminSnapTradeSyncResult> {
  return post(`/api/admin/snaptrade/support-users/${userId}/sync`, {})
}

export async function updateAdminBrokerSupportUser(
  userId: string,
  action: 'pause' | 'resume' | 'mark_reconnect_required' | 'archive'
): Promise<{ status: string; connections_updated: number; accounts_updated: number }> {
  return post(`/api/admin/snaptrade/support-users/${userId}/${action}`, {})
}

export async function deleteAdminSnapTradeProviderConnection(connectionId: string): Promise<{
  status: string
  connectionId: string
  providerDeleteStatus: string
}> {
  return post(`/api/admin/snaptrade/connections/${connectionId}/provider-delete`, {})
}

export async function createAdminSnapTradeConnectionPortal(): Promise<{
  url: string
  expiresInSeconds: number
  sessionId: string | null
}> {
  return post('/api/admin/snaptrade/connect', {})
}

export async function resetAdminSnapTradeConnection(): Promise<{
  url: string
  expiresInSeconds: number
  sessionId: string | null
}> {
  return post('/api/admin/snaptrade/reset', {})
}

export async function archiveStaleAdminSnapTradeMappings(): Promise<{
  archivedConnections: number
  archivedAccounts: number
}> {
  return post('/api/admin/snaptrade/archive-stale', {})
}

export async function getAdminSnapTradeConnections(params?: {
  refresh?: boolean
}): Promise<{ connections: AdminSnapTradeConnection[] }> {
  return get('/api/admin/snaptrade/connections', params)
}

export async function getAdminSnapTradeAccounts(params?: {
  refresh?: boolean
  authorizationId?: string
}): Promise<{ accounts: AdminSnapTradeAccount[] }> {
  return get('/api/admin/snaptrade/accounts', params)
}

export async function syncAdminSnapTradeAccounts(params?: {
  accountId?: string
  authorizationId?: string
}): Promise<AdminSnapTradeSyncResult> {
  return post('/api/admin/snaptrade/sync', params ?? {})
}

export async function getAdminSnapTradeImportPreview(params?: {
  limit?: number
}): Promise<AdminSnapTradeImportPreview> {
  return get('/api/admin/snaptrade/import-preview', params)
}

export async function linkAdminSnapTradeAccountToPortfolio(
  brokerAccountId: string,
  payload: {
    portfolioId: string
    importMode: string
    importStartDate?: string | null
    optionPriorState?: string | null
    autoImportEnabled?: boolean
    autoPostHighConfidenceEvents?: boolean
    requireReviewForOptions?: boolean
  }
): Promise<{ account: AdminSnapTradeAccount }> {
  return post(`/api/admin/snaptrade/accounts/${brokerAccountId}/link-portfolio`, payload)
}

export async function previewAdminSnapTradeHistoricalPlan(
  brokerAccountId: string,
  payload: {
    portfolioId?: string | null
    importMode?: string | null
    importStartDate?: string | null
    optionPriorState?: string | null
    eventLimit?: number
  }
): Promise<AdminSnapTradeHistoricalPlan> {
  return post(`/api/admin/snaptrade/accounts/${brokerAccountId}/historical-plan/preview`, payload)
}

export async function applyAdminSnapTradeHistoricalPlan(
  planId: string,
  payload: { dryRun?: boolean; batchSize?: number } = {}
): Promise<{
  status: string
  planId: string
  eventCount: number
  postedEventIds: string[]
  remainingEventCount?: number
  postableEventIds?: string[]
  repairCount?: number
  repairedEventIds?: string[]
}> {
  return post(`/api/admin/snaptrade/historical-plans/${planId}/apply`, payload)
}

export async function postAdminSnapTradeBrokerEvent(
  eventId: string
): Promise<{ status: string; eventId: string; reason?: string | null }> {
  return post(`/api/admin/snaptrade/broker-events/${eventId}/post`, {})
}

export async function ignoreAdminSnapTradeBrokerEvent(
  eventId: string
): Promise<{ status: string; eventId: string }> {
  return post(`/api/admin/snaptrade/broker-events/${eventId}/ignore`, {})
}

// ============================================================================
// Broker Sync API Methods
// ============================================================================

export async function getBrokerSyncConnections(): Promise<BrokerSyncConnectionsResponse> {
  return get<BrokerSyncConnectionsResponse>('/api/broker-sync/connections')
}

export async function getCustomerBrokerSyncStatus(): Promise<CustomerBrokerSyncStatus> {
  return get<CustomerBrokerSyncStatus>('/api/broker-sync/status')
}

export async function connectCustomerBroker(
  client: BrokerSyncPortalClient = 'web'
): Promise<CustomerBrokerPortalResponse> {
  return post<CustomerBrokerPortalResponse>(
    '/api/broker-sync/connect',
    client === 'native' ? { client } : undefined
  )
}

export async function refreshCustomerBrokerData(): Promise<CustomerBrokerRefreshResponse> {
  return post<CustomerBrokerRefreshResponse>('/api/broker-sync/refresh')
}

export async function refreshCustomerBrokerDataAfterLogin(): Promise<CustomerBrokerRefreshResponse> {
  return post<CustomerBrokerRefreshResponse>('/api/broker-sync/login-refresh')
}

export async function reconnectCustomerBroker(
  client: BrokerSyncPortalClient = 'web'
): Promise<CustomerBrokerPortalResponse> {
  return post<CustomerBrokerPortalResponse>(
    '/api/broker-sync/reconnect',
    client === 'native' ? { client } : undefined
  )
}

export async function disconnectCustomerBroker(): Promise<{
  status: string
  message: string
  providerDeletionPerformed: boolean
}> {
  return post('/api/broker-sync/disconnect')
}

export async function linkCustomerBrokerAccountPortfolio(
  accountId: string,
  payload:
    | {
        mode: 'create_new'
        portfolioName: string
      }
    | {
        mode: 'existing'
        portfolioId: string
        acknowledgeExistingPortfolioHasData: boolean
        acknowledgeSharedPortfolio: boolean
      }
): Promise<{
  accountId: string
  linkedPortfolio: CustomerBrokerPortfolio
  portfolioHasData: boolean
  mappingStatus: string
  message: string
}> {
  return post(`/api/broker-sync/accounts/${accountId}/link-portfolio`, payload)
}

export async function configureCustomerBrokerImportPlan(
  accountId: string,
  payload: {
    importMode: 'import_from_date' | 'snapshot_baseline_future'
    importStartDate: string
    assumeNoPriorOptionPositions: boolean
  }
): Promise<{
  accountId: string
  importMode: 'import_from_date' | 'snapshot_baseline_future'
  importStartDate: string
  assumeNoPriorOptionPositions: boolean
  importStatus: string
  message: string
}> {
  return post(`/api/broker-sync/accounts/${accountId}/import-plan`, payload)
}

export async function previewCustomerBrokerImport(
  accountId: string
): Promise<CustomerBrokerImportPreview> {
  return post(`/api/broker-sync/accounts/${accountId}/import-preview`)
}

export async function commitCustomerBrokerImport(
  accountId: string
): Promise<CustomerBrokerImportCommit> {
  return post(`/api/broker-sync/accounts/${accountId}/import-commit`)
}

export async function reconnectCustomerBrokerConnection(
  connectionId: string,
  client: BrokerSyncPortalClient = 'web'
): Promise<CustomerBrokerPortalResponse> {
  return post(
    `/api/broker-sync/connections/${connectionId}/reconnect-portal`,
    client === 'native' ? { client } : undefined
  )
}

export async function removeCustomerBrokerConnection(connectionId: string): Promise<{
  status: string
  providerDeletionStatus: string
  providerDeletionPerformed: boolean
  message: string
}> {
  return post(`/api/broker-sync/connections/${connectionId}/remove`)
}

export async function getBrokerSyncAccounts(): Promise<BrokerSyncAccountsResponse> {
  return get<BrokerSyncAccountsResponse>('/api/broker-sync/accounts')
}

export async function reconnectBrokerSyncConnection(
  connectionId: string
): Promise<BrokerSyncReconnectResponse> {
  return post<BrokerSyncReconnectResponse>(`/api/broker-sync/connections/${connectionId}/reconnect`)
}

export async function runBrokerSyncConnection(
  connectionId: string,
  params: {
    sync_type:
      | 'manual_refresh'
      | 'incremental'
      | 'reconnect_refresh'
      | 'reconcile'
      | 'initial_backfill'
    allow_existing_initial_import?: boolean
  }
): Promise<BrokerSyncRunResponse> {
  return post<BrokerSyncRunResponse>(`/api/broker-sync/sync/${connectionId}`, {
    sync_type: params.sync_type,
    allow_existing_initial_import: params.allow_existing_initial_import ?? true,
  })
}

export async function updateIbkrTransactionSettings(
  accountId: string,
  payload: IbkrTransactionSettingsUpdateRequest
): Promise<IbkrTransactionSettingsUpdateResponse> {
  return patch<IbkrTransactionSettingsUpdateResponse>(
    `/api/broker-sync/accounts/${accountId}/ibkr-transaction-settings`,
    payload
  )
}

export async function linkIbkrConnection(payload: {
  cpapi_base_url?: string
  display_name?: string
  attach_portfolio_id?: string
  override_populated_portfolio?: boolean
}): Promise<BrokerSyncLinkResult> {
  return post<BrokerSyncLinkResult>('/api/broker-sync/link/callback', {
    provider: 'ibkr',
    payload,
  })
}

export async function deleteBrokerSyncConnection(
  connectionId: string
): Promise<{ connection_id: string; status: string }> {
  return del<{ connection_id: string; status: string }>(
    `/api/broker-sync/connections/${connectionId}`
  )
}

// ============================================================================
// Exports
// ============================================================================

/**
 * API client with all methods
 */
type StripeApi = {
  getPricing: typeof getPricing
  getSubscriptionStatus: typeof getSubscriptionStatus
  verifyTrialStarted: typeof verifyTrialStarted
  getTrialUsageStatus: typeof getTrialUsageStatus
  createCheckoutSession: typeof createCheckoutSession
  cancelCheckout: typeof cancelCheckout
  createPortalSession: typeof createPortalSession
  cancelSubscription: typeof cancelSubscription
  convertTrialToPaid: typeof convertTrialToPaid
}

const stripeApi: StripeApi = NATIVE_MOBILE_BUILD
  ? {
      getPricing: getPricing,
      getSubscriptionStatus: getSubscriptionStatus,
      verifyTrialStarted: nativeSubscriptionPaymentActionDisabled,
      getTrialUsageStatus: getTrialUsageStatus,
      createCheckoutSession: nativeSubscriptionPaymentActionDisabled,
      cancelCheckout: nativeSubscriptionPaymentActionDisabled,
      // Existing Stripe subscribers may manage their current plan from iOS/Android.
      // This is not a purchase or upgrade call to action.
      createPortalSession: createPortalSession,
      cancelSubscription: nativeSubscriptionPaymentActionDisabled,
      convertTrialToPaid: nativeSubscriptionPaymentActionDisabled,
    }
  : {
      getPricing: getPricing,
      getSubscriptionStatus: getSubscriptionStatus,
      verifyTrialStarted: verifyTrialStarted,
      getTrialUsageStatus: getTrialUsageStatus,
      createCheckoutSession: createCheckoutSession,
      cancelCheckout: cancelCheckout,
      createPortalSession: createPortalSession,
      cancelSubscription: cancelSubscription,
      convertTrialToPaid: convertTrialToPaid,
    }

export const api = {
  performance: {
    getCalendar: getPerformanceCalendar,
    addClosingPrice: addPerformanceClosingPrice,
    exportCalendar: exportPerformanceCalendar,
  },
  // Portfolios
  portfolios: {
    getAll: getPortfolios,
    getById: getPortfolio,
    getPerformance: getPortfolioTimeframePerformance,
    create: createPortfolio,
    update: updatePortfolio,
    delete: deletePortfolio,
    setDefault: setDefaultPortfolio,
  },

  // Positions
  positions: {
    getAll: getPositions,
    getById: getPosition,
    create: createPosition,
    update: updatePosition,
    close: closePosition,
    delete: deletePosition,
    deleteAll: deleteAllPositions,
    deleteAllOpen: deleteAllOpenPositions,
    deleteAllClosed: deleteAllClosedPositions,
    repairContractMetadata: repairPositionContractMetadata,
  },

  edge: {
    getReport: getEdgeReport,
  },

  // Dashboard
  dashboard: {
    getCurrent: getDashboardCurrent,
    getMetrics: getDashboardMetrics,
    getHistoricalPnl: getHistoricalPnl,
    getHistoricalPnlV3: getHistoricalPnlV3,
    getAggregateAll: getAggregateAll,
  },

  // CSV Import/Export
  csv: {
    upload: uploadCSV,
    confirmImport: confirmImport,
    export: exportCSV,
  },

  // Auth
  auth: {
    getProfile: getProfile,
    deleteAccount: deleteAccount,
    resetPortfolioData: resetPortfolioData,
  },

  funnel: {
    saveSignupAttribution,
    trackEvent: trackFunnelEventRequest,
    getMeasurementOutcomes,
    submitTrialFeedback: submitTrialActivationFeedback,
  },

  adminFunnel: {
    getReport: getAdminFunnelReport,
  },

  // Lifecycle
  lifecycle: {
    exerciseLongCall: exerciseLongCall,
    exerciseLongPut: exerciseLongPut,
    markAssignedOrExercised: markAssignedOrExercised,
  },

  // Position events
  positionEvents: {
    getAll: getPositionEvents,
  },

  // Cash Ledger
  cashLedger: {
    getEntries: getCashLedgerEntries,
    getSummary: getCashLedgerSummary,
    createEntry: createCashLedgerEntry,
    delete: deleteCashLedgerEntry,
    deleteAll: deleteCashLedgerEntries,
  },

  // Market Data (Premium)
  marketData: {
    getStatus: getMarketDataStatus,
    refresh: refreshMarketData,
    getHistoricalPrices: getHistoricalPrices,
  },

  // Alert Preferences (Premium)
  alerts: {
    getPreferences: getAlertPreferences,
    updatePreferences: updateAlertPreferences,
  },

  notificationPreferences: {
    get: getNotificationPreferences,
    update: updateNotificationPreferences,
  },

  notifications: {
    list: getNotifications,
    markRead: markNotificationRead,
  },

  mobile: {
    registerPushToken,
    revokeCurrentPushToken,
    sendTestPush: sendTestPushNotification,
  },

  emailPreferences: {
    get: getEmailPreferences,
    update: updateEmailPreferences,
  },

  // Broker Sync
  brokerSync: {
    getStatus: getCustomerBrokerSyncStatus,
    connect: connectCustomerBroker,
    refresh: refreshCustomerBrokerData,
    refreshAfterLogin: refreshCustomerBrokerDataAfterLogin,
    reconnect: reconnectCustomerBroker,
    disconnect: disconnectCustomerBroker,
    linkAccountPortfolio: linkCustomerBrokerAccountPortfolio,
    configureImportPlan: configureCustomerBrokerImportPlan,
    previewImport: previewCustomerBrokerImport,
    commitImport: commitCustomerBrokerImport,
    reconnectConnectionPortal: reconnectCustomerBrokerConnection,
    removeConnection: removeCustomerBrokerConnection,
    getConnections: getBrokerSyncConnections,
    getAccounts: getBrokerSyncAccounts,
    reconnectConnection: reconnectBrokerSyncConnection,
    runConnectionSync: runBrokerSyncConnection,
    updateIbkrTransactionSettings,
    linkIbkrConnection,
    deleteConnection: deleteBrokerSyncConnection,
  },

  scenarioLab: {
    analyze: analyzeScenario,
    payoff: (request: Omit<ScenarioAnalyzeRequest, 'portfolio_id'>) =>
      post<{
        trade: ScenarioAnalysis['trade']
        analyzed_input_fingerprint: string
        analysis_reference?: string
      }>('/api/scenario-lab/payoff', request),
    list: listScenarios,
    get: getScenario,
    create: createScenario,
    update: updateScenario,
    duplicate: duplicateScenario,
    delete: deleteScenario,
    prepareOrder: prepareScenarioOrder,
    trackingCapabilities: (instrumentTypes: Array<'stock' | 'option'> = []) => {
      const query = new URLSearchParams()
      instrumentTypes.forEach((instrumentType) => query.append('instrument_type', instrumentType))
      return get<ScenarioTrackingCapabilities>(
        `/api/scenario-lab/tracking-capabilities${query.size ? `?${query}` : ''}`
      )
    },
    trackingDiagnostics: (id: string) =>
      get<ScenarioTrackingDiagnostics>(`/api/scenario-lab/${id}/tracking/diagnostics`),
    tracking: (id: string) => get<ScenarioTracking>(`/api/scenario-lab/${id}/tracking`),
    trackingObservations: (id: string, options: { cursor?: string; page_size?: number } = {}) => {
      const params = new URLSearchParams()
      if (options.cursor) params.set('cursor', options.cursor)
      if (options.page_size !== undefined) params.set('page_size', String(options.page_size))
      const query = params.toString()
      return get<ScenarioTrackingObservationPage>(
        `/api/scenario-lab/${id}/tracking/observations${query ? `?${query}` : ''}`
      )
    },
    refreshTracking: (id: string) =>
      post<ScenarioTrackingObservation>(`/api/scenario-lab/${id}/tracking/refresh`),
    trackingLifecycle: (
      id: string,
      request: { action: 'pause' | 'resume' | 'archive'; expected_status?: string }
    ) => post<ScenarioTracking>(`/api/scenario-lab/${id}/tracking/lifecycle`, request),
    closeTracking: (
      id: string,
      request: {
        prices: Array<{ instrument_key: string; price: number; source: string; timestamp: string }>
        expected_status?: string
      }
    ) => post<ScenarioTracking>(`/api/scenario-lab/${id}/tracking/close`, request),
    settleTracking: (
      id: string,
      request: {
        instrument_key: string
        underlying_price: number
        source: string
        timestamp: string
        assumption: 'exercise_if_itm' | 'expire_worthless'
        acknowledge_simulation: true
        expected_status?: string
      }
    ) => post<ScenarioTracking>(`/api/scenario-lab/${id}/tracking/settlement`, request),
  },

  // Underlying (Stock) Positions
  underlyingPositions: {
    getAll: getUnderlyingPositions,
    getById: getUnderlyingPosition,
    create: createUnderlyingPosition,
    update: updateUnderlyingPosition,
    close: closeUnderlyingPosition,
    deleteAll: deleteAllUnderlyingPositions,
    deleteAllOpen: deleteAllOpenUnderlyingPositions,
    deleteAllClosed: deleteAllClosedUnderlyingPositions,
    delete: deleteUnderlyingPosition,
    recordTrade: recordStockTrade,
    getTradeHistory: getStockTradeHistory,
    getTrades: getStockTrades,
    getTradeById: getStockTradeById,
  },

  // Stripe Subscription
  stripe: stripeApi,

  revenueCat: {
    syncSubscription: syncRevenueCatSubscription,
    getAcquisitionEligibility,
    reserveAcquisition,
    releaseAcquisition,
  },

  affiliate: {
    getPublicProgram: getAffiliatePublicProgram,
    getLeaderboard: getAffiliateLeaderboard,
    captureReferral: captureAffiliateReferral,
    claimReferral: claimAffiliateReferral,
    join: joinAffiliateProgram,
    getDashboard: getAffiliateDashboard,
    updateReferralCode: updateAffiliateReferralCode,
    createConnectAccount: createAffiliateConnectAccount,
    createOnboardingLink: createAffiliateOnboardingLink,
    createDashboardLoginLink: createAffiliateDashboardLoginLink,
    submitFeatureRequest: submitAffiliateFeatureRequest,
  },

  affiliateAdmin: {
    getOverview: getAffiliateAdminOverview,
    updateSettings: updateAffiliateAdminSettings,
    runPayoutBatch: runAffiliatePayoutBatch,
    sendLaunchCampaign: sendAffiliateLaunchCampaign,
    sendEngagedInviteCampaign: sendAffiliateEngagedInviteCampaign,
    sendQuarterlyUpdateCampaign: sendAffiliateQuarterlyUpdateCampaign,
    updateStatus: updateAffiliateStatus,
    updateReferralCode: adminUpdateAffiliateReferralCode,
    reviewReferral: reviewAffiliateReferral,
    updateFeatureRequestStatus: updateAffiliateFeatureRequestStatus,
  },

  // Strategies
  strategies: {
    getAll: getStrategies,
    getAllWithLegs: getStrategiesWithLegs,
    getById: getStrategy,
    create: createStrategy,
    update: updateStrategy,
    delete: deleteStrategy,
    addLeg: addStrategyLeg,
    removeLeg: removeStrategyLeg,
    close: closeStrategy,
    validate: validateStrategy,
    getTypes: getStrategyTypes,
    getTypeMetadata: getStrategyTypeMetadata,
  },

  // Options Screener
  screener: {
    getOptionsChain: getOptionsChain,
    getOptionContract: getOptionContract,
    getQuote: getQuote,
    /**
     * Get filtered, paginated options for screener
     */
    getScreenerOptions: (
      symbol: string,
      params: {
        dte_min?: number
        dte_max?: number
        delta_min?: number
        delta_max?: number
        iv_min?: number
        iv_max?: number
        volume_min?: number
        oi_min?: number
        strike_min?: number
        strike_max?: number
        bid_min?: number
        bid_max?: number
        ask_min?: number
        ask_max?: number
        moneyness_min?: number
        moneyness_max?: number
        option_type?: string
        liquidity?: string
        moneyness?: string
        limit?: number
        cursor?: string
        sort_by?: string
        sort_dir?: string
      }
    ) =>
      get<ScreenerResponse>(
        `/api/market/options/${symbol.toUpperCase()}/screener`,
        params as Record<string, string | number>
      ),
  },

  // Options Discovery Scans (Premium)
  scans: {
    list: listScans,
    execute: executeScan,
    preview: getScanPreview,
    savedScreens: {
      list: listSavedDiscoveryScreens,
      create: createSavedDiscoveryScreen,
      update: updateSavedDiscoveryScreen,
      duplicate: duplicateSavedDiscoveryScreen,
      delete: deleteSavedDiscoveryScreen,
      recordRun: recordSavedDiscoveryScreenRun,
    },
  },

  // Feedback System
  feedback: {
    submitBug: submitBugReport,
    submitFeature: submitFeatureRequest,
    submitInactive: submitInactiveUserFeedback,
    getFeatures: getFeatureRequests,
    getFeature: getFeatureRequest,
    toggleVote: toggleFeatureVote,
    uploadScreenshot: uploadFeedbackScreenshot,
    checkAdminStatus: checkFeedbackAdminStatus,
    updateStatus: updateFeatureStatus,
  },

  // Admin - Scan Snapshots (Admin Only)
  admin: {
    checkIsAdmin: checkAdminStatus,
    getSnapshotDates: getAdminSnapshotDates,
    getSnapshots: getAdminSnapshots,
    getSnapshotStats: getAdminSnapshotStats,
    getSnapshotAnomalies: getAdminSnapshotAnomalies,
    getAllTopPicks: getAdminAllTopPicks,
    getOutcomeAnalytics: getAdminOutcomeAnalytics,
    getOutcomeRecompute: getAdminOutcomeRecompute,
    getOutcomeRecomputeRange: getAdminOutcomeRecomputeRange,
    runScanJobs: runAdminScanJobs,
    getBacktestRuns: getAdminBacktestRuns,
    getBacktestRun: getAdminBacktestRun,
    runAssetFundamentals: runAssetFundamentalsJob,
    emailCampaigns: {
      list: getAdminEmailCampaigns,
      create: createAdminEmailCampaign,
      update: updateAdminEmailCampaign,
      estimate: estimateAdminEmailCampaign,
      preview: previewAdminEmailCampaign,
      sendTest: sendAdminEmailCampaignTest,
      queue: queueAdminEmailCampaign,
      cancel: cancelAdminEmailCampaign,
    },
    snapTrade: {
      getStatus: getAdminSnapTradeStatus,
      getSupportUsers: getAdminBrokerSyncSupportUsers,
      syncSupportUser: syncAdminBrokerSupportUser,
      updateSupportUser: updateAdminBrokerSupportUser,
      deleteProviderConnection: deleteAdminSnapTradeProviderConnection,
      createConnectionPortal: createAdminSnapTradeConnectionPortal,
      resetConnection: resetAdminSnapTradeConnection,
      archiveStale: archiveStaleAdminSnapTradeMappings,
      getConnections: getAdminSnapTradeConnections,
      getAccounts: getAdminSnapTradeAccounts,
      syncAccounts: syncAdminSnapTradeAccounts,
      getImportPreview: getAdminSnapTradeImportPreview,
      linkAccountToPortfolio: linkAdminSnapTradeAccountToPortfolio,
      previewHistoricalPlan: previewAdminSnapTradeHistoricalPlan,
      applyHistoricalPlan: applyAdminSnapTradeHistoricalPlan,
      postBrokerEvent: postAdminSnapTradeBrokerEvent,
      ignoreBrokerEvent: ignoreAdminSnapTradeBrokerEvent,
    },
  },
}

export default api
